<template>
  <div class="fts-list-view-2">
    <div class="list-view__container inistate-scroller" ref="list-view-container">
      <template v-if="listViewLoaded">
        <template v-if="!listingError">
          <div class="list-view__header" v-if="isDesignMode">
            <div class="list-view__header-emoji">
              <fts-emoji 
                append-to-body 
                :toggle-size="28" 
                v-model="designModuleEmoji" 
                @input="onDesignModuleEmojiInput"
              />
            </div>

            <div class="list-view__header-info">
              <fts-contenteditable 
                :search="true" 
                v-model="designModuleName" 
                placeholder="Name this module" 
                data-testid="lite-studio-module-name"
                class="list-view__header-name list-view__header-name--editable" 
                @input="$v.designModuleData.name.$touch()"
                @input:search="designSetModuleEmoji" 
              />

              <fts-contenteditable 
                v-model="designModuleDescription" 
                placeholder="Give this page a description (optional)" 
                class="list-view__header-description list-view__header-description--editable" 
              />

              <div class="list-view__header-error-message" v-if="isDesignModuleNameError">
                {{ isDesignModuleNameError }}
              </div>
            </div>

            <fts-tippy
              :content="isDefaultView ? 'Design this page' : 'Edit view panel'"
              class="list-view__header-action"
              v-if="(listingId && listingId !== 'everything') || isDefaultView"
            >
              <font-awesome-icon :icon="['fas', 'pencil']" fixed-width
                @click="isDefaultView ? openDesignListingDrawer({carrier: true}) : openDesignListingDrawer({listingId})" />
            </fts-tippy>
          </div>

          <div class="list-view__header" v-else>
            <div class="list-view__header-emoji">
              <fts-emoji 
                append-to-body 
                :toggle-size="28" 
                :disabled="true" 
                :emoji="headerInfo.emoji" 
              />
            </div>

            <div class="list-view__header-info">
              <div class="list-view__header-name">
                {{headerInfo.name}}
              </div>

              <div class="list-view__header-description">
                {{headerInfo.description}}
              </div>
            </div>
          </div>
        </template>

        <div class="list-view__content">
          <!-- AutoCount Full's lock (402 connector_full_required): not an error -->
          <fts-connector-lock-card :lock="listingLock" v-if="listingLock" />
          <div class="list-view__error" v-else-if="listingError">
            <div class="list-view__error-title">Something went wrong</div>
            <div class="list-view__error-message">{{listingError}}</div>
          </div>

          <template v-else>
            <div :style="contentElWidthStyle">
              <div class="list-view__content-header" >
                <div class="list-view__content-header__left">
                  <template v-if="isMiniStudio && isDesignMode">
                    <div class="view-bar">
                      <div class="view-tabs-scroll">
                        <div class="view-tabs">
                          <div
                            class="view-tab"
                            :class="{ 'view-tab__active': 'everything' === listingId }"
                            @click="switchListView('everything')"
                          >
                            <span class="name">All</span>
                          </div>

                          <div
                            class="view-tab"
                            v-for="list in listingData.listing"
                            :key="list.id"
                            :class="{ 'view-tab__active': list.id === listingId }"
                            @click="switchListView(list.id)"
                          >
                            <fts-emoji
                              v-if="list.emoji"
                              :emoji="list.emoji"
                              :toggle-size="18"
                              :disabled="true"
                              class="emoji"
                            />
                            <span class="name">{{ list.name }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- 固定在右侧 -->
                      <div class="view-tab-actions" v-if="isModuleReady">
                        <span class="material-icons-outlined view-tab-action" @click="openDesignListingDrawer">settings</span>
                        <span class="material-icons-outlined view-tab-action" @click="openDesignListingDrawer({ new: true })">add</span>
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <!-- create -->
                    <fts-btn 
                      color="primary" 
                      text-transform-init 
                      box-shadow-none 
                      class="fts-font-normal" 
                      data-click-stop 
                      @click="openCreateForm" 
                      v-if="isCreatable"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" />New
                    </fts-btn>
                    <!-- activities -->
                    <fts-btn
                      v-for="activity in activityList"
                      :key="activity.id"
                      outline
                      :color="activity.color ? activity.color : 'primary'"
                      :emoji="activity.emoji"
                      text-transform-init
                      class="btn-activity"
                      data-click-stop  
                      @click="
                        executeActivity(
                          activity,
                          resultSet.list.filter((x) => x.selected)
                        )
                      "
                    >
                      <span class="btn-activity-label">{{ activity.name }}
                        <fts-test-badge v-if="activity.development" :stage="activity.development"></fts-test-badge>
                      </span>
                      <font-awesome-icon
                        v-if="isDesignMode"
                        :icon="['fas', 'ellipsis-vertical']"
                        class="btn-activity-action"
                        @click.stop.prevent="openDesignWorkflowDrawer({'tab':'action','action':'edit','data':activity})"
                      />
                    </fts-btn>
                    <!-- documents -->
                    <fts-btn 
                      v-for="action in documentList" 
                      :key="action.id" 
                      outline 
                      color="ternary-green" 
                      text-transform-init 
                      class="btn-activity" 
                      data-click-stop 
                      @click="
                        executeDocument(
                          action,
                          resultSet.list.filter((x) => x.selected)
                        )
                        "
                      >
                      <font-awesome-icon :icon="['far', 'file-pdf']" class="btn-activity-icon fts-file-pdf"
                        v-if="action.typeId === 'pdf'" />
                      <font-awesome-icon :icon="['far', 'file-alt']" class="btn-activity-icon fts-file-excel"
                        v-else-if="action.typeId === 'spreadsheet'" />
                      <font-awesome-icon :icon="['far', 'file-code']" class="btn-activity-icon fts-file-html"
                        v-else-if="action.typeId === 'html'" />
                      <font-awesome-icon :icon="['far', 'file']" class="btn-activity-icon fts-file-default" v-else />
                      <span class="btn-activity-label">{{ action.name }}
                        <fts-test-badge v-if="action.development" :stage="action.development"></fts-test-badge>
                      </span>
                    </fts-btn>
                    <!-- actions -->
                    <fts-btn outline :color="action.color ? action.color : 'ternary-blue'" :emoji="action.emoji"
                      text-transform-init class="btn-activity" v-for="action in actionList" :key="action.id" @click="
                        executeAction(
                          action,
                          resultSet.list.filter((x) => x.selected)
                        )
                        ">
                      <span :class="{ 'btn-activity-label': !action.color }">
                        {{ action.name }}
                      </span>
                    </fts-btn>
                    <!-- standard -->
                    <fts-btn 
                      v-for="activity in bulkActivities" 
                      :key="activity.id" 
                      outline 
                      text-transform-init 
                      class="btn-activity" 
                      :color="activity.id === 'delete' ? 'danger' : 'primary'" 
                      data-click-stop 
                      @click="
                        executeActivity(
                          activity,
                          resultSet.list.filter((x) => x.selected)
                        )
                      "
                    >
                      <i :class="activity.icon"></i> {{ activity.name }}
                    </fts-btn>

                    <fts-tippy
                      triggerClass="sidenav-menu-outer"
                      content="Create new activity"
                      :options="{ placement: 'right' }"
                      class="sidenav-menu"
                    >
                      <div
                      class="activities-design-toggle-create"
                        v-if="isDesignMode"
                        @click="
                          openDesignWorkflowDrawer({'tab':'action','action':'create'})
                        "
                      >
                        <font-awesome-icon :icon="['fas', 'plus']" fixed-width/>
                      </div>
                    </fts-tippy>
                  </template>
                </div>
              </div>

              <div class="divider" v-if="!(isMiniStudio && isDesignMode)" />

              <div class="list-view__content-secondary-header">
                  <div class="table-search">
                    <input
                      class="fts-form-control"
                      placeholder="Search"
                      v-model="searchTerm"
                      @input="onSearchInput"
                    />
                    <div class="table-search-action table-search-action--search">
                      <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                    </div>
                  </div>
                  <div class="list-view__content-secondary-header-actions">
                    <fts-special-btn @click="openDesignWorkflowDrawer({'tab':'action'})" v-if="isMiniStudio && isDesignMode && isModuleReady">
                      <span class="material-symbols-outlined" style="height: 16px; width: 16px; font-size: 16px;">account_tree</span>
                      Workflows
                    </fts-special-btn>

                    <fts-filter v-model="filters" :columns="columns" :states="states"
                      :modules="workspaceModules"
                    ></fts-filter>

                    <fts-sort
                    :modules="workspaceModules"
                    :fields="columns"
                    v-model="sort"
                    :total-items-exceed="isTotalItemsExceed"
                    @closed="retrieveItems"
                    v-if="isCapableOf(SYSTEM_CAPABILITY.Sort)"
                    append-to-body
                    />

                    <fts-btn 
                      text 
                      icon 
                      text-transform-init 
                      @click="!loading && retrieveItems()"
                    >
                      <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="loading" fixed-width />
                    </fts-btn>
                  </div>
              </div>

              <fts-query-designer
                :style="contentElWidthStyle"
                :modules="workspaceModules"
                :states="states"
                :information-source="filterColumns"
                :filter="filters"
                mode="view"
                class="list-view__content-filter"
                v-if="filters && filters.items.length"
              />
            </div>

            <div class="table-outter" :style="contentElWidthStyle">
              <div class="table-container" ref="tableContainer">
                <table class="fts-data-table">
                  <thead class="data-table__head">
                    <draggable
                      :disabled="!isDesignMode || resizeIndex !== null"
                      v-model="sortableInformation"
                      v-bind="dragOptions"
                      @end="onInformationSortEnd"
                      class="draggable data-table__row"
                      item-key="id"
                      tag="tr"
                      data-v-step="all-tasks"
                      handle=".handle"
                    >
                      <template
                        v-for="(column, colIndex) in sortableInformation"
                      >
                        <th
                          :key="`design-${column.id}`"
                          class="data-table__cell data-table__cell--header data-table__cell--design"
                          :class="{
                            'data-table__cell--primary-cell': colIndex == 0,
                            'other-col-resizing': resizeIndex != null && resizeIndex !== colIndex,
                          }"
                          :style="{ width: columnWidth(column) }"
                          v-if="isDesignMode && (!column.system || column.id == 'state')"
                        >
                          <div class="data-table__label-group">
                            <span class="material-symbols-outlined data-table__handle handle">drag_indicator</span>

                            <fts-label-group
                              :item="{
                                ...column,
                                displayName: column.label || column.displayName,
                              }" 
                              information 
                              :information-modules="workspaceModules" 
                              emoji 
                              :emoji-size="14" 
                              class="data-table__label"
                            ></fts-label-group>
                            
                            <div 
                              class="data-table__sort" 
                              :class="{'data-table__sort--ghost': isSortingHeader(column) === undefined}" 
                              @click="()=>onInformationHeader(column)"
                            > 
                              <font-awesome-icon :icon="['fas', 'sort-up']" fixed-width v-if="isSortingHeader(column) === 'ascending'" />
                              <font-awesome-icon :icon="['fas', 'sort-down']" fixed-width v-else-if="isSortingHeader(column) === 'descending'" />
                              <font-awesome-icon :icon="['fas', 'sort']" fixed-width v-else />
                            </div>

                            <div class="actions-group" v-if="isModuleReady">
                              <span class="material-symbols-outlined actions-group-action" @click.prevent="( column.id == 'state')? openDesignWorkflowDrawer({'tab':'state'}) :openColumnDrawer(column)">edit</span>
                              <fts-form-design-shortcut
                                ref="ftsAddColumnShortcut"
                                :modules="designWorkspaceModules"
                                :options="designModuleInformation"
                                :selectedOptions="designSelectedInformation"
                                :is-lite="true"
                                suggestion
                                placement="bottom-start"
                                @option:selected="evt => designAddField(evt, colIndex + 1)"
                                @option:create="evt => designCreateField(evt, colIndex + 1)"
                                v-if="isDesignMode"
                              >
                                <span class="material-symbols-outlined actions-group-action">add</span>
                              </fts-form-design-shortcut>
                            </div>
                            <span
                                class="resize-handle"
                                :class="{
                                  'resize-handle-dragging': resizeIndex === colIndex,
                                }"
                                @mousedown="startResize($event, colIndex)"
                              ></span>
                          </div>
                        </th>

                        <th
                          :key="column.id"
                          class="data-table__cell data-table__cell--header"
                          :class="{
                            'data-table__cell--primary-cell': colIndex == 0
                          }"
                          :style="{ width: columnWidth(column) }"
                          v-else
                        >
                          <div class="data-table__label-group">
                            <fts-label-group
                              :item="{
                                ...column,
                                displayName: column.label || column.displayName,
                              }" 
                              information 
                              :information-modules="workspaceModules" 
                              emoji 
                              :emoji-size="14" 
                              class="data-table__label"
                            ></fts-label-group>

                            <div 
                              class="data-table__sort" 
                              :class="{'data-table__sort--ghost': isSortingHeader(column) === undefined}" 
                              @click="()=>onInformationHeader(column)"
                            > 
                              <font-awesome-icon :icon="['fas', 'sort-up']" fixed-width v-if="isSortingHeader(column) === 'ascending'" />
                              <font-awesome-icon :icon="['fas', 'sort-down']" fixed-width v-else-if="isSortingHeader(column) === 'descending'" />
                              <font-awesome-icon :icon="['fas', 'sort']" fixed-width v-else />
                            </div>
                          </div>
                        </th>
                      </template>

                      <th class="data-table__cell data-table__cell--header data-table__cell--header" v-if="!(sortableInformation && sortableInformation.length)">
                      <th class="data-table__cell data-table__cell-placeholder"></th>
                      <th
                        ref="addColumnHeaderCell"
                        class="data-table__cell data-table__cell--header data-table__cell--design data-table__cell-action data-table__cell-action__sticky data-table__cell-action__sticky--header"
                        v-if="(isDesignMode && isModuleReady) || (resultSet.list.length && isDeleteRight)"
                      >
                        <fts-form-design-shortcut
                          ref="ftsAddColumnShortcut"
                          :modules="designWorkspaceModules"
                          :options="designModuleInformation"
                          :selectedOptions="designSelectedInformation"
                          :is-lite="true"
                          suggestion
                          placement="bottom-start"
                          @option:selected="evt => designAddField(evt, colIndex + 1)"
                          @option:create="evt => designCreateField(evt, colIndex + 1)"
                          :exclude-types="[6, 8, 9, 10, 12, 14, 15, 19, 20, 21, 22, 24, 28, 29, 30, 31, 32, 33, 35, 36, 37]"
                          v-if="isDesignMode"
                        >
                          <span ref="addColumnHeaderIcon" class="material-icons-outlined action-icon">add</span>
                        </fts-form-design-shortcut>
                      </th>
                    </draggable>
                  </thead>

                  <tbody class="data-table__body" v-if="!isViewEmpty">
                    <tr class="data-table__row" v-if="isMiniStudio">
                      <td 
                        v-for="(column, colIndex) in sortableInformation && sortableInformation.length || 1" 
                        :key="column" 
                        class="data-table__cell data-table__cell--inline-header"
                      >
                        <fts-btn
                          text
                          text-transform-init
                          data-click-stop
                          @click="addNewRowInline"
                          v-if="colIndex == 0"
                        >
                          <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 0.875rem;"/>
                          New row
                        </fts-btn> 
                      </td>
                      <td class="data-table__cell data-table__cell--inline-header data-table__cell-placeholder"></td>
                      <td class="data-table__cell data-table__cell-action__sticky" v-if="isDesignMode || isDeleteRight"></td>
                    </tr>

                    <tr
                      v-for="item in resultSet.list"
                      :key="item.id"
                      :data-entry-id="item.id"
                      class="data-table__row"
                      :class="{ 'data-table__row--selected': item.selected }"
                      data-click-stop 
                      @click="selectRow(item)"
                    >
                      <td
                        v-for="(column, colIndex) in sortableInformation && sortableInformation.length ? sortableInformation : [{ primaryField: true }]"
                        :key="colIndex"
                        class="data-table__cell"
                        :class="{ 'data-table__cell--primary-cell': column.primaryField }"
                      >
                        <template v-if="column.id">
                          <div class="data-table_cell__handle handle" v-if="colIndex == 0">
                            <span class="material-symbols-outlined handle-icon">drag_indicator</span>
                          </div> 

                          <template v-if="column.system">
                            <!-- document id -->
                            <div v-if="column.fieldName === 'documentId'">
                              {{ item.documentId }}
                            </div>
                            <!-- state -->
                            <template v-else-if="column.fieldName === 'state'" >
                              <div class="data-table__cell-state" v-if="isMiniStudio" >
                                <fts-states-design-dropdown
                                  :states="states" 
                                  :selected-state="item.state" 
                                  disable-blank-option
                                  disable-assign-design
                                  design-mode
                                  @option:create="(val) => changeState(item, val, 'create')"
                                  @option:select="(val) => changeState(item, val, 'select')"
                                  @option:clear="changeState(item)"
                                  @state:update="(val) => updateState(val)"
                                  @state:delete="(val) => deleteState(val)"
                                />
                                
                              </div>
                              <fts-state :state="item.state" :states="states" :tint="true" class="data-table__status" v-else/>
                            </template>
                            <!-- end state -->
                            
                            <!-- system date -->
                            <div v-else-if="column.fieldName === 'date'">
                              {{ moment(item.date).format("ll") }}
                            </div>
                            <!-- created date -->
                            <div v-else-if="column.fieldName === 'createdDate'">
                              {{ moment(item.audit.createdDate).format("llll") }}
                            </div>
                            <!-- created By -->
                            <div class="fts-audit" v-else-if="column.fieldName === 'createdBy'">
                              <fts-profile :userName="item.audit.createdBy" class="audit-profile" />
                              <fts-tippy class="audit-date-wrapper" :content="moment(item.audit.createdDate).format('llll')">
                                <div slot="trigger" class="audit-date">
                                  {{ momentRelativeTime(item.audit.createdDate) }}
                                </div>
                              </fts-tippy>
                            </div>
                            <!-- updated date -->
                            <div v-else-if="column.fieldName === 'lastChangeDate'">
                              {{ moment(item.audit.updatedDate).format("llll") }}
                            </div>
                            <!-- updated by -->
                            <div class="fts-audit" v-else-if="column.fieldName === 'lastChangeBy'">
                              <fts-profile :userName="item.audit.updatedBy" class="audit-profile" />
                              <fts-tippy class="audit-date-wrapper" :content="moment(item.audit.updatedDate).format('llll')">
                                <div slot="trigger" class="audit-date">
                                  {{ momentRelativeTime(item.audit.updatedDate) }}
                                </div>
                              </fts-tippy>
                            </div>
                            <!-- assign to -->
                            <fts-assign-to
                              :module-id="moduleId"
                              :form-data="item"
                              :view-mode="!isAssignRight"
                              :comment-section="isCommentRight"
                              class="stopPropagation"
                              @refresh="refresh"
                              v-else-if="column.fieldName === 'assignTo'"
                            />
                            <!-- username -->
                            <div v-else-if="column.fieldName === 'username'">
                              <fts-profile
                                :user-name="item.extension.username"
                                profile-group
                                v-if="item.extension && item.extension.username"
                              />
                            </div>
                          </template>

                          <template v-else-if="isMiniStudio">
                            <fts-inline-edit ref="inlineEditRef" :moduleId="moduleId" :listingId="listingId" activityId="manage" v-model="item.extension[column.fieldName]" :entryId="item.id" :extension="item" :column="column" ></fts-inline-edit>
                          </template>

                          <fts-list-view-display
                            :form-data="item.extension || item"
                            :item="column"
                            :vector-id="moduleId"
                            enable-text-alignment
                            v-else
                          ></fts-list-view-display>
                        </template>
                        <!-- SS05909: why this row came back, when the field it matched on is not
                             one of this listing's columns. On a column the reader can already see,
                             the row explains itself and the hint would be noise. -->
                        <div class="search-match-hint" v-if="colIndex == 0 && hiddenSearchMatch(item)">
                          matched on {{ hiddenSearchMatch(item) }}
                        </div>
                        <!-- shortcut -->
                        <div class="data-table__cell-shortcut" v-if="colIndex == 0">
                          <div class="shortcut-container shortcut-container-comment" v-if="item.commentCount">
                            <span class="material-icons-outlined shortcut-container-icon">mode_comment</span>{{ item.commentCount }}
                          </div>

                          <div 
                            class="shortcut-container shortcut-container-intention" 
                            v-if="item.lastIntention && item.lastIntention.id"
                          >
                            <span class="material-symbols-outlined material-symbols-outlined--filled shortcut-container-icon">warning</span>
                          </div>

                          <div class="shortcut-container shortcut-container-btn" @click="openViewForm(item)" v-if="isQuickViewRight || isViewRight">
                            <span class="material-icons-outlined shortcut-container-icon">visibility</span> 
                            View
                          </div>

                          <fts-standard-menu
                            :item="item"
                            :standard-actions="standardActivities"
                            :read-only="Boolean(header && header.readOnlySource)"
                            :module-id="moduleId"
                            :list-id="listingId"
                            class="shortcut-container stopPropagation"
                            @executed="evt => executeActivity(evt, [item], {
                              beforeOpen: () => setRow(item, true)
                            })"
                            :is-lite="true"
                          >
                            <div slot="toggle" class="shortcut-container-btn-icon">
                              <span class="material-icons-outlined shortcut-container-icon">more_vert</span>
                            </div>
                          </fts-standard-menu>
                        </div>
                      </td>
                      <td class="data-table__cell data-table__cell-placeholder"></td>
                      <td class="data-table__cell data-table__cell-action__sticky" v-if="isDesignMode || isDeleteRight">
                        <span class="material-icons-outlined action-icon stopPropagation" @click="onEntryDelete(item)">delete</span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="list-view-empty" ref="listViewEmpty" v-if="isViewEmpty">
                  <div class="list-view-empty__icon">
                    <img :src="emptyRecordImage" alt="empty-views-image">
                  </div>

                  <div class="list-view-empty__title">Nothing here</div>

                  <div class="list-view-empty__description">
                    <div>Currently doesn't have any entries.</div>
                    <div>Add a column first or tap + to create new entry.</div>
                  </div>

                  <fts-btn 
                    color="primary" 
                    box-shadow-none 
                    text-transform-init 
                    @click="onCreateFromEmptyView" 
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    New
                  </fts-btn>
                </div>

                <div
                  class="list-view-empty-column-hint"
                  :style="emptyColumnHintStyle"
                  v-if="showEmptyColumnHint"
                >
                  <svg :viewBox="emptyColumnHintViewBox" aria-hidden="true" focusable="false">
                    <defs>
                      <mask id="list-view-empty-column-hint-mask" maskUnits="userSpaceOnUse">
                        <rect class="list-view-empty-column-hint__mask" x="0" y="0" width="100%" height="100%" />
                      </mask>
                      <marker
                        id="list-view-empty-column-hint-arrowhead"
                        markerWidth="18"
                        markerHeight="24"
                        refX="18"
                        refY="12"
                        orient="auto"
                        markerUnits="userSpaceOnUse"
                      >
                        <path class="list-view-empty-column-hint__head" d="M0 0 L18 12 L0 24 Z" />
                      </marker>
                    </defs>
                    <path class="list-view-empty-column-hint__curve" :d="emptyColumnHintPath" mask="url(#list-view-empty-column-hint-mask)" />
                  </svg>
                  <div class="list-view-empty-column-hint__text">Add a column first</div>
                </div>
              </div>
            </div>

            <fts-pagination
              :pagination="isCapableOf(SYSTEM_CAPABILITY.Pagination)"
              :total-items="totalItems"
              :total-items-limit="totalItemslimit"
              :filtered="Boolean(filters && filters.items && filters.items.length)"
              v-model="pageSize"
              class="list-view__content-pagination"
              :style="contentElWidthStyle"
              @pageChanged="pageChanged"
              @page-size-changed="setPageSizePreference"
              v-if="!isViewEmpty && (isCapableOf(SYSTEM_CAPABILITY.Pagination) || isCapableOf(SYSTEM_CAPABILITY.NextPage))"
            ></fts-pagination>
          </template>
        </div>
      </template>

      <div class="list-view__content" v-else-if="listingError">
        <fts-connector-lock-card :lock="listingLock" v-if="listingLock" />
        <div class="list-view__error" v-else>
          <div class="list-view__error-title">Something went wrong</div>
          <div class="list-view__error-message">{{listingError}}</div>
        </div>
      </div>
    </div>

    <fts-list-view-form ref="ftsListViewForm"></fts-list-view-form>
    <fts-spinner full-screen boundary="parent" v-if="loading" />
    <fts-action-page-modal ref="ftsActionPageModal" />
    <fts-document-modal ref="ftsDocumentModal" />
    <fts-document-form-modal ref="ftsDocumentFormModal" />
    <fts-design-workflow-drawer
      :workflow-data="workflowData"
      :module-id="moduleId"
      ref="ftsDesignWorkflowDrawer"
    />
    <fts-design-listing-drawer
      :listing-data="listingData"
      :module-id="moduleId"
      ref="ftsDesignListingDrawer"
    />
    <fts-information-design-modal client-mode ref="ftsInformationDesignModal" />
    <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
    <fts-information-design-drawer ref="ftsInformationDesignDrawer" />
  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import moment from 'moment';
import changesets from 'diff-json';
import draggable from 'vuedraggable';
import numeral from 'numeral';

import commons from '../../commons/commons';
import { 
  SYSTEM_CAPABILITY, 
  isCapableOf, 
  Listing_Max_Total_Entries,
  Lite_Listing_Pagination_Total_Items_Threshold
} from '../../commons/flags';
import queryLogic from '../../commons/queryLogic';
import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import { classificationStore } from '../../store/classificationStore';
import { store as canvasDesignStore } from "../../store/canvasDesignStore";
import LocalStorageService from '../../store/LocalStorageService';
import { activityPortalId } from '../../portals/activityPortal';
import { openActivityPortalHere } from '../../portals/activityPortalHere';
import listing from '../../commons/listing';
import designValidator from '../../commons/designValidator';
import canvasDesign from '../../commons/canvasDesign';
import moduleGuard from '../../commons/moduleGuard';
import { emptyRecordImage } from '../../commons/images';

import FTSBtn from '../FTSBtn';
import FTSLabelGroup from '../FTSLabelGroup';
import FTSListViewDisplay from '../FTSListViewDisplay';
import FTSPagination from '../../lite/FTSPagination';
import FTSProfile from '../FTSProfile';
import FTSSpinner from '../FTSSpinner';
import FTSConnectorLockCard from '../FTSConnectorLockCard';
import { connectorLockFromError } from '../connectorLock';
import FTSState from '../FTSState';
import FTSDocumentModal from '../FTSDocumentModal';
import FTSDocumentFormModal from '../FTSDocumentFormModal';
import FTSTestBadge from '../FTSTestBadge';
import FTSActionPageModal from '../FTSActionPageModal';
import FTSTippy from '../FTSTippy';
import FTSStandardMenu from '../FTSStandardMenu';
import FTSListViewForm from './FTSListViewForm';
import FTSAssignTo from '../FTSAssignTo';
import FTSSort from '../../lite/FTSSort';
import FTSFilter from '../../lite/FTSFilter';
import FTSContenteditable from '../FTSContenteditable';
import FTSEmoji from '../FTSEmoji';
import FTSDesignWorkflowDrawer from '../../design/FTSDesignWorkflowDrawer';
import FTSDesignListingDrawer from '../../design/FTSDesignListingDrawer';
import FTSFormDesignShortcut from '../../design/FTSFormDesignShortcut';
import FTSInformationDesignModal from '../../design/FTSInformationDesignModal';
import FTSInformationDeleteConfirmation from '../../design/FTSInformationDeleteConfirmation';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSInformationDesignDrawer from '../../design/FTSInformationDesignDrawer';
import FTSQueryDesigner from '../../lite/FTSQueryDesigner';
import FTSStatesDesignDropdown from '../../design/FTSStatesDesignDropdown';
import FTSInlineEdit from '../../lite/FTSInlineEdit';
import FTSSpecialBtn from '../../lite/FTSSpecialBtn';
import { entryStateId } from '../../commons/entryState';
import { hiddenSearchMatch } from '../../commons/searchMatchHint';

const listViewModes = [
  { id: "list", code: "l" },
  { id: "card", code: "c" },
  { id: "board", code: "b" },
  { id: "calendar", code: "d" },
  { id: "detail", code: "dv" }
];
const BULK_MAX_COUNT = 50;
const BULK_EXCEED_ERROR = `Selected items exceeded. Maximum only allows to select up to ${BULK_MAX_COUNT}.`;

export default {
  props: {
    client: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    draggable,
    'fts-btn': FTSBtn,
    'fts-label-group': FTSLabelGroup,
    'fts-list-view-display': FTSListViewDisplay,
    'fts-pagination': FTSPagination,
    'fts-profile': FTSProfile,
    'fts-query-designer': FTSQueryDesigner,
    'fts-spinner': FTSSpinner,
    'fts-connector-lock-card': FTSConnectorLockCard,
    'fts-state': FTSState,
    'fts-document-modal': FTSDocumentModal,
    'fts-document-form-modal': FTSDocumentFormModal,
    'fts-test-badge': FTSTestBadge,
    'fts-action-page-modal': FTSActionPageModal,
    'fts-tippy': FTSTippy,
    'fts-standard-menu': FTSStandardMenu,
    'fts-list-view-form': FTSListViewForm,
    'fts-assign-to': FTSAssignTo,
    'fts-sort': FTSSort,
    'fts-filter': FTSFilter,
    'fts-contenteditable': FTSContenteditable,
    'fts-emoji': FTSEmoji,
    'fts-design-workflow-drawer': FTSDesignWorkflowDrawer,
    'fts-design-listing-drawer': FTSDesignListingDrawer,
    'fts-form-design-shortcut': FTSFormDesignShortcut,
    'fts-information-design-modal': FTSInformationDesignModal,
    'fts-information-delete-confirmation': FTSInformationDeleteConfirmation,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-information-design-drawer': FTSInformationDesignDrawer,
    'fts-states-design-dropdown': FTSStatesDesignDropdown,
    'fts-inline-edit': FTSInlineEdit,
    'fts-special-btn': FTSSpecialBtn
  },
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    resizeObserver: null,
    autoSave: null,
    designAIEmojiRequestId: 0,
    contentElWidthStyle: null,
    mode: "list",
    header: {},
    moduleId: null,
    listingId: null,
    listingError: null,
    // the module is on AutoCount Full and this workspace is not (402): a lock card, not the error
    listingLock: null,
    columns: [],
    preferences: [],
    originalDefaultFilters: null,
    filters: null,
    skipFiltersWatch: false,
    sort: null,
    resultSet: { list: [] },
    pageSize: 200,
    page: 0,
    loading: false,
    listViewLoaded: false,
    autoRefreshInterval: null,
    bulk: false,
    dragOptions: {
      animation: 200,
      ghostClass: 'ghost'
    },
    sortableInformation: [],
    resizeIndex: null,
    searchTerm: "",
    moduleReadyPromise: null,
    moduleReadyResolve: null,
    moduleReadyResolveTimeout: null,
    isLeavingRoute: false,
    emptyColumnHintPosition: null,
    emptyColumnHintPath: '',
    emptyColumnHintViewBox: '0 0 1 1',
    emptyColumnHintAnimationFrame: null,
    emptyRecordImage
  }),
  validations() {
    const moduleList = canvasDesignStore.getters.workspaceModules
    const menusList = canvasDesignStore.getters.canvasMenus

    return {
      designModuleData: {
        ...designValidator.moduleDesign(moduleList),
          extension: {
            menus: {
              $each: designValidator.menuDesign(menusList)
            }
          }

      }
    }
  },
  methods: {
    moment,
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    isCapableOf(type) {
      return isCapableOf(type, this.workspaceModules, this.moduleId);
    },
    isRightOf(type, list) {
      return list.indexOf(type) > -1;
    },
    resetScroll() {
      const el = this.$refs["list-view-container"];
      if (el) {
        el.scrollTo({ top: 0, left: 0 });
      }
    },
    redirectToModuleHub() {
      this.$router.replace({ name: "lite-module-hub" });
    },
    redirectToFirstAvailableModule(routeName = this.$route.name) {
      const firstOrDefaultMenu = commons.getFirstOrDefaultMenu(clientStore.getters.workspaceMenus);

      if (!firstOrDefaultMenu) return;

      this.moduleId = firstOrDefaultMenu.moduleId;
      this.listingId = firstOrDefaultMenu.listingId;

      const routeToPush = {
        name: routeName,
        params: { moduleId: commons.encodeids(this.moduleId) }
      };

      if (this.listingId) {
        routeToPush.query = { l: this.listingId };
      }

      this.$router.push(routeToPush);
    },
    getRouteListingId(route = this.$route) {
      if (route && route.name === "lite-studio") {
        return route.query && route.query.l ? route.query.l : "everything";
      }

      return route && route.query && route.query.l ? route.query.l : null;
    },
    getListViewLoadErrorMessage(err) {
      if (err && err.isCustom && err.message) {
        return err.message;
      }

      const responseMessage = err?.response?.data?.message;

      if (responseMessage) {
        return responseMessage;
      }

      return "Unable to load the module. Please try again.";
    },
    async loadRouteModuleState(route = this.$route) {
      const routeQueryActivityId = route.query?.a ?? null;
      const routeQueryListingId = this.getRouteListingId(route);
      const routeQueryEntryId = route.query && route.query.id ? route.query.id : null;

      this.loading = true;
      this.listViewLoaded = false;
      this.listingError = null;
      this.listingLock = null;

      this.listingId = routeQueryListingId;

      const preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
      if (preference && preference.pageSize) {
        this.pageSize = preference.pageSize;
      }

      try {
        await clientStore.dispatch("loadWorkspaceModule", {
          moduleId: this.moduleId,
          listId: this.listingId
        });
        canvasDesignStore.dispatch("setUsers", this.workspaceUsers);
        classificationStore.dispatch("setUsers", { users: this.workspaceUsers });

        if (this.moduleId != clientStore.getters.moduleId) {
          throw {
            isCustom: true,
            message: "Module isn't available. The module you're trying to access is unpublished or have been removed."
          };
        }

        if (!route.query.dialog) {
          this.initListViewMode({
            code: "l",
            moduleId: this.moduleId,
            listingId: this.listingId
          });

          await this.initialSchema();
          this.listViewLoaded = true;

          if (
            routeQueryEntryId ||
            (routeQueryActivityId === "create" && this.isCreatable)
          ) {
            let formEntryId = null;
            let formModuleId = this.moduleId;
            let formListingId = routeQueryListingId;

            if (routeQueryEntryId) {
              const queryDecodeids = commons.queryDecodeids(routeQueryEntryId);
              if (queryDecodeids) {
                if (queryDecodeids.entryId) {
                  formEntryId = queryDecodeids.entryId;
                }

                if (queryDecodeids.moduleId) {
                  formModuleId = queryDecodeids.moduleId;

                  if (this.moduleId != formModuleId) {
                    formListingId = null;
                  }
                } else if (queryDecodeids.moduleUnspecified) {
                  /* Wildcard connection link: the entry is not this module's. Let the server
                     resolve the module from the entry instead of asserting ours (SS05672). */
                  formModuleId = null;
                  formListingId = null;
                }
              }
            }

            window.eventBus.$emit("listViewForm:open", {
              id: formEntryId,
              moduleId: formModuleId,
              listingId: formListingId,
              activityId: routeQueryActivityId,
              title: this.getFormTitle(formEntryId, routeQueryActivityId)
            });

            this.selectRowById(formEntryId, { skipForm: true });
          }
        }
      } catch (err) {
        console.error(err);
        this.listingError = this.getListViewLoadErrorMessage(err);
        this.listingLock = connectorLockFromError(err);
      } finally {
        this.loading = false;
        await this.$nextTick();
        this.scheduleEmptyColumnHintPositionUpdate();
        this.resolveModuleReadySoon();
      }
    },
    resolveModuleReadySoon() {
      if (this.isLeavingRoute || this._isBeingDestroyed || this._isDestroyed) return;

      clearTimeout(this.moduleReadyResolveTimeout);
      this.moduleReadyResolveTimeout = setTimeout(() => {
        this.moduleReadyResolve && this.moduleReadyResolve();
        this.moduleReadyResolveTimeout = null;
      }, 100);
    },
    async loadWorkspaceList(payload = {}) {
      const request = {
        moduleId: this.moduleId,
        listingId: this.listingId,
        withHeader: (payload && payload.withHeader) || false,
        currentPage: this.page,
        filters: (payload && payload.filters) || null,
        sorts: this.sort ? this.sort : null,
        contextId: clientStore.state.workspaceContext != null ?
          clientStore.state.workspaceContext.id :
          null,
        search: payload.search
      };

      if (!(payload && payload.omitPageSize)) {
        request.pageSize = this.pageSize;
      }

      return await clientStore.dispatch("loadWorkspaceList", request);
    },
    setFiltersInternally(filters) {
      this.debouncedFilters.cancel();
      this.skipFiltersWatch = true;
      this.filters = filters;
      this.$nextTick(() => {
        this.skipFiltersWatch = false;
      });
    },
    onSearchInput() {
      this.debouncedSearch.cancel();
      this.debouncedSearch();
    },
    setLocalStoragePreferenceFilters(preference, filters) {
      let savedPreference = { ...preference };

      if (
        filters &&
        Array.isArray(filters.items) &&
        filters.items.length > 0
      ) {
        savedPreference.filters = filters;
      } else {
        delete savedPreference.filters;
      }

      LocalStorageService.setModuleListingPreference(
        this.moduleId,
        this.listingId,
        savedPreference
      );
    },
    /* SS05684: a saved preference replays the design-owned units (script / formula) with the
       definition they had when it was saved, and the server runs the script the client posts.
       Re-seat those units from the definition the header just delivered, keep the user's own
       units and values, persist, and requery - only when that actually changed something. */
    async reseatDesignOwnedFilters(preference, definitionFilters) {
      const reseated = listing.refreshDesignOwnedUnits(this.filters, definitionFilters);
      if (!reseated.changed) {
        return null;
      }

      this.setFiltersInternally(reseated.filters);
      this.setLocalStoragePreferenceFilters(preference, reseated.filters);
      return this.loadWorkspaceList({ filters: reseated.filters });
    },
    clearLocalStoragePreferenceFilters() {
      let preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
      if (preference) {
        delete preference.filters;

        LocalStorageService.setModuleListingPreference(
          this.moduleId,
          this.listingId,
          preference
        );
      }

      let defaultFilters = { "items": [] };
      if (this.originalDefaultFilters) {
        defaultFilters = this.originalDefaultFilters;
      }

      this.setFiltersInternally(defaultFilters);
      this.refresh();
    },
    setInitialFilters() {
      let preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);

      if (preference && preference.filters) {
        if (
          Array.isArray(preference.filters.items) &&
          preference.filters.items.length
        ) {
          this.setFiltersInternally(preference.filters);
        } else {
          this.setLocalStoragePreferenceFilters(preference, null);
        }
      }
    },
    composeFilterColumns(columns) {
      let filterColumns = _.cloneDeep(columns);
      // collaboration
      filterColumns.splice(1, 0, {
        ...queryLogic.querySelectionPrefix.find(x => x.id === "collaboration")
      });
      // assign to
      const assignToIndex = filterColumns.findIndex(x => x.id === "assignTo");
      if (assignToIndex > -1) {
        const isDueExisted = filterColumns.some(x => x.id === "due");
        if (!isDueExisted) {
          filterColumns.splice(assignToIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "due")
          });
        }
      }
      // created date
      const createdByIndex = filterColumns.findIndex(x => x.id === "createdBy");
      if (createdByIndex > -1) {
        const isCreatedDateExisted = filterColumns.some(x => x.id === "createdDate");
        if (!isCreatedDateExisted) {
          filterColumns.splice(createdByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "createdDate")
          });
        }
      }
      // last change date
      const lastChangeByIndex = filterColumns.findIndex(x => x.id === "lastChangeBy");
      if (lastChangeByIndex > -1) {
        const isLastChangeDateExisted = filterColumns.some(x => x.id === "lastChangeDate");
        if (!isLastChangeDateExisted) {
          filterColumns.splice(lastChangeByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "lastChangeDate")
          });
        }
      }

      return filterColumns.filter(column => {
        if (column.system) return true;

        const informationType = commons.informationTypes.find(x => x.id === column.type);
        return Boolean(informationType && informationType.searchable);
      });
    },
    setPageSizePreference(value) {
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId),
        pageSize: value
      };
      LocalStorageService.setModuleListingPreference(this.moduleId, this.listingId, preference);
    },
    hasActiveFilter(filters) {
      return Boolean(
        filters &&
        Array.isArray(filters.items) &&
        filters.items.length
      );
    },
    /* SS05909: the field a searched row matched on, shown only when this listing does not already
       display it. The rule lives in commons/searchMatchHint so it can be read and tested on its
       own. */
    hiddenSearchMatch(item) {
      return hiddenSearchMatch(item, this.columns, this.searchTerm);
    },
    clearPageSizePreference() {
      const preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);

      if (preference && preference.pageSize) {
        delete preference.pageSize;
        LocalStorageService.setModuleListingPreference(this.moduleId, this.listingId, preference);
      }
    },
    async normalizePageSizeResult(res, payload = {}) {
      const resultSet = res && res.data && res.data.data;
      const totalItems = resultSet && resultSet.totalItems;

      if (!commons.isInteger(totalItems)) {
        return res;
      }

      if (totalItems > Lite_Listing_Pagination_Total_Items_Threshold) {
        return res;
      }

      if (!this.hasActiveFilter(this.filters) && !this.searchTerm) {
        this.clearPageSizePreference();
      }

      if (payload.omitPageSize) {
        return res;
      }

      if (Array.isArray(resultSet.list) && resultSet.list.length >= totalItems) {
        return res;
      }

      this.page = 0;
      return await this.loadWorkspaceList({
        filters: this.filters,
        search: this.searchTerm,
        omitPageSize: true
      });
    },
    async refresh(payload = {}) {
      const withHeader = Boolean(payload && payload.withHeader);
      const withPreferenceFilters = Boolean(payload && payload.withPreferenceFilters);
      const preserveSelectedEntries = Boolean(payload && payload.preserveSelectedEntries);
      const inlineAcknowledgedAtRequest = this.getInlineAcknowledgedRevisions();
      const refreshGeneration = this._inlineGeneration;
      const refreshModuleId = this.normalizeInlineEntryId(this.moduleId);
      const refreshListingId = this.normalizeInlineEntryId(this.listingId);
      const isCurrentRefresh = () =>
        refreshGeneration === this._inlineGeneration &&
        refreshModuleId === this.normalizeInlineEntryId(this.moduleId) &&
        refreshListingId === this.normalizeInlineEntryId(this.listingId);

      try {
        let res = null;

        if (withHeader) {
          let preference = null;
          let isPreferenceFilters = false;

          if (withPreferenceFilters) {
            preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
            isPreferenceFilters = Boolean(preference && preference.filters);
          }

          try {
            res = await this.loadWorkspaceList({ withHeader: true, filters: this.filters });
          } catch (err) {
            if (!isCurrentRefresh()) return;

            if (isPreferenceFilters) {
              isPreferenceFilters = false;
              this.setLocalStoragePreferenceFilters(preference, null);

              res = await this.loadWorkspaceList({ withHeader: true });
            } else {
              throw err;
            }
          }

          if (!isCurrentRefresh()) return;

          const resultData = _.cloneDeep(res.data);

          if (isPreferenceFilters) {
            const filterColumns = this.composeFilterColumns(resultData.header.columns);
            let clonedFilters = _.cloneDeep(this.filters);
            queryLogic.updateQueryFilters(filterColumns, clonedFilters);
            const diff = changesets.diff(clonedFilters, this.filters);

            if (diff && diff.length) {
              if (
                !Array.isArray(clonedFilters.items) ||
                !clonedFilters.items.length
              ) {
                clonedFilters = null;
              }

              try {
                res = await this.loadWorkspaceList({ filters: clonedFilters });
              } catch (err) {
                if (!isCurrentRefresh()) return;

                clonedFilters = null;
                res = await this.loadWorkspaceList();
              }

              if (!isCurrentRefresh()) return;

              if (clonedFilters) {
                this.setFiltersInternally(clonedFilters);
                this.setLocalStoragePreferenceFilters(preference, clonedFilters);
              } else {
                this.setFiltersInternally({
                  items: [
                    queryLogic.getDefaultQueryFilter(filterColumns, { workspaceBeta: clientStore.getters.workspaceBeta })
                  ]
                });

                isPreferenceFilters = false;
                this.setLocalStoragePreferenceFilters(preference, null);
              }
            }
          }

          if (!isCurrentRefresh()) return;

          this.header = resultData.header;
          this.columns = resultData.header.columns;
          this.moduleImport = resultData.import;

          if (
            this.header.defaultFilters &&
            this.header.defaultFilters.items &&
            this.header.defaultFilters.items.length
          ) {
            const { filters, originalDefaultFilters, defaultFilterChanged } = listing.setDefaultFilters(
              this.header.defaultFilters,
              this.originalDefaultFilters,
              this.filters,
              this.columns
            );

            this.originalDefaultFilters = originalDefaultFilters;

            if (!isPreferenceFilters) {
              this.setFiltersInternally(filters);

              if (this.schemaLoad && defaultFilterChanged) {
                res = await this.loadWorkspaceList({ filters: filters });
                if (!isCurrentRefresh()) return;
              }
            } else if (defaultFilterChanged) {
              const reseated = await this.reseatDesignOwnedFilters(preference, this.header.defaultFilters);
              if (!isCurrentRefresh()) return;
              if (reseated) res = reseated;
            }
          } else if (isPreferenceFilters) {
            // the definition has no units left: a preference must not keep replaying old ones
            const reseated = await this.reseatDesignOwnedFilters(preference, { items: [] });
            if (!isCurrentRefresh()) return;
            if (reseated) res = reseated;
          } else if (!this.schemaLoad) {
            this.setFiltersInternally({
              items: [
                // queryLogic.getDefaultQueryFilter(this.columns, { workspaceBeta: clientStore.getters.workspaceBeta })
              ]
            });
            this.originalDefaultFilters = null;
          }
          // default sort
          if (this.header.defaultSort) {
            this.sort = _.cloneDeep(this.header.defaultSort);
          }
          // system capability
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
            this.header.standards = this.header.standards.filter(x =>
              ["create", "edit", "changeStatus", "duplicate", "assign"].indexOf(x) === -1
            );
          }
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
            this.header.standards = this.header.standards.filter(x => x !== "delete");
          }
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
            this.header.standards = this.header.standards.filter(x => ["view", "quickView"].indexOf(x) === -1);
          }
          // preferences
          this.originalPreferences = this.header.preferences;
          this.preferences = this.header.preferences;
          if (this.preferences && this.preferences.length) {
            const diffColumns = _.differenceBy(this.columns, this.preferences, 'id').map(x => ({ id: x.id }));
            const diffPreferences = _.differenceBy(this.preferences, this.columns, 'id');
            if (diffColumns.length) {
              this.preferences = this.preferences.concat(diffColumns);
            }
            if (diffPreferences.length) {
              diffPreferences.forEach(x => {
                this.preferences.splice(this.preferences.indexOf(x), 1);
              });
            }
          }
          // auto refresh
          if (this.autoRefreshInterval) {
            clearInterval(this.autoRefreshInterval);
            this.autoRefreshInterval = null;
          }

          if (this.header && this.header.autoUpdate && !this._isBeingDestroyed && !this._isDestroyed) {
            this.autoRefreshInterval = setInterval(async () => {
              if (!this.loading) {
                const cloneSelectedIds = this.formData.filter((x) => x.selected).map((x) => x.id);
                await this.retrieveItems();
                if (cloneSelectedIds) {
                  cloneSelectedIds.forEach((x) => {
                    let res = this.formData.find((y) => y.id === x);
                    res && this.$set(res, "selected", true);
                  });
                }
              }
            }, 5000);
          }

          this.schemaLoad = true;
        } else {
          res = await this.loadWorkspaceList({ filters: this.filters, search: this.searchTerm });
        }

        if (!isCurrentRefresh()) return;

        res = await this.normalizePageSizeResult(res, payload);
        if (!isCurrentRefresh()) return;

        let resultSet = res.data.data;

        if (
          preserveSelectedEntries &&
          this.resultSet &&
          this.resultSet.list &&
          this.resultSet.list.length
        ) {
          const selectedIds = {};
          for (let i = 0; i < this.resultSet.list.length; i++) {
            const item = this.resultSet.list[i];
            if (item.selected) selectedIds[item.id] = true;
          }

          for (let i = 0; i < resultSet.list.length; i++) {
            if (selectedIds[resultSet.list[i].id]) {
              resultSet.list[i].selected = true;
            }
          }
        }

        this.applyInlineDraftOverlay(resultSet, inlineAcknowledgedAtRequest);
        this.resultSet = resultSet;
        this.listingError = null;
        this.listingLock = null;
      } catch (err) {
        if (!isCurrentRefresh()) return;

        let errorMessage = "Unable to load the list. Please try again.";

        if (
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          errorMessage = err.response.data.message;
        }

        this.listingError = errorMessage;
        this.listingLock = connectorLockFromError(err);
        console.error(err);
      }
    },
    async initialSchema() {
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }

      this.searchTerm = '';
      this.setInitialFilters();
      await this.refresh({ withHeader: true, withPreferenceFilters: true });
    },
    setListViewMode(mode, { moduleId, listingId } = {}) {
      this.mode = listViewModes.some((x) => x.id === mode) ? mode : "list";
      if (moduleId) {
        let preference = {
          ...LocalStorageService.getModuleListingPreference(moduleId, listingId),
          type: this.mode
        }

        LocalStorageService.setModuleListingPreference(moduleId, listingId, preference);
      }
      this.resetScroll();
    },
    initListViewMode({ code, moduleId, listingId } = {}) {
      let mode = null;
      if (this.client && code) {
        const listViewMode = listViewModes.find((x) => x.code === code.toLowerCase());
        Boolean(listViewMode && listViewMode.id) && (mode = listViewMode.id);
      }
      if (!mode) {
        const listViewPreference = LocalStorageService.getModuleListingPreference(moduleId, listingId);
        Boolean(listViewPreference && listViewPreference.type) && (mode = listViewPreference.type);
      }
      this.setListViewMode(mode, { moduleId, listingId });
    },
    async retrieveItems(payload) {
      this.loading = true;
      await this.refresh(payload);
      this.loading = false;
      if (this._focusInlineEditEntryId) {
        const entryId = this._focusInlineEditEntryId;
        this._focusInlineEditEntryId = null;
        this.$nextTick(() => {
          const refs = this.$refs.inlineEditRef;
          const all = Array.isArray(refs) ? refs : (refs ? [refs] : []);
          const target = all.find(r => String(r.$props.entryId) === String(entryId));
          if (target && target.focusField) target.focusField();
        });
      }
    },
    async pageChanged(page) {
      this.page = page;
      await this.retrieveItems();
      this.resetScroll();
    },
    nextDesignAIEmojiRequestId() {
      this.designAIEmojiRequestId += 1;
      return this.designAIEmojiRequestId;
    },
    onDesignModuleEmojiInput() {
      this.nextDesignAIEmojiRequestId();
    },
    async switchListView(listingId, removedListingId) {
      if(removedListingId && this.listingId !== removedListingId){
        return;
      }

      try {
        await this.autoSave.flush();
        await this.flushInlineSaves();
      } catch (error) {
        return;
      }

      this.nextDesignAIEmojiRequestId();
      this.listingId = listingId;

       await this.$router.replace({
            name: this.$route.name,
            params: {
              ...this.$route.params,
            },
            query: {
              ...this.$route.query,
              l: this.listingId
            }
          });

      this.initListViewMode({ 
        code: 'l', 
        moduleId: this.moduleId, 
        listingId: listingId
      });

      await this.initialSchema();
    },
    getFormTitle(entryId, activityId) {
      if (activityId === "create") return;
      if (!Array.isArray(this.columns) || !this.columns.length) return;
      if (!Array.isArray(this.resultSet.list) || !this.resultSet.list.length) return;

      let firstColumn = null;
      const systemColumns = new Set(["documentId", "state", "createdDate", "createdBy", "lastChangeDate", "lastChangeBy", "username"]);
      const nonSystemColumns = new Set(["0", "2", "3", "4", "5", "6", "7", "9", "16", "20", "25", "26", "27", "33"]);

      for (const column of this.columns) {
        if (column.system) {
          if (systemColumns.has(column.fieldName)) {
            firstColumn = column;
            break;
          }
        } else {
          if (nonSystemColumns.has(String(column.type))) {
            firstColumn = column;
            break;
          }
        }
      }

      if (!firstColumn) return;

      const formData = this.resultSet.list.find(x => x.id === entryId);
      if (!formData) return;

      const normalize = (value) => {
        if (typeof value === "string") return value.trim();
        if (typeof value === "number") return String(value);
        return null;
      };

      // handle system fields
      if (firstColumn.system) {
        const audit = formData.audit || {};
        const findUserByUsername = (username) => {
          if (!username) return null;

          const user = commons.findUserByUsername(this.workspaceUsers, username);
          if (user && user.displayName) {
            return normalize(user.displayName);
          }

          return normalize(username);
        };
        const systemFieldMap = {
          documentId: () => normalize(formData.documentId),
          state: () => formData.state ? normalize(formData.state.name) : null,
          createdDate: () => audit.createdDate ? moment(audit.createdDate).format("llll") : null,
          createdBy: () => findUserByUsername(audit.createdBy),
          lastChangeDate: () => audit.updatedDate ? moment(audit.updatedDate).format("llll") : null,
          lastChangeBy: () => findUserByUsername(audit.updatedBy),
          username: () => {
            if (formData.extension && formData.extension.username) {
              return findUserByUsername(formData.extension.username);
            }

            return null;
          }
        }

        if (systemFieldMap[firstColumn.fieldName]) {
          return systemFieldMap[firstColumn.fieldName]();
        }

        return null;
      }

      // handle fields
      const result = commons.model(formData.extension, firstColumn.fieldName);
      if (result === undefined || result === null) return null;
      // text, integer, module, multiline, user, email, phone number, select
      const stringTypes = new Set(["0", "2", "7", "16", "20", "25", "26", "27"]);
      if (stringTypes.has(String(firstColumn.type))) {
        return normalize(result);
      }
      // number
      if (firstColumn.type == 3) {
        if (firstColumn.customFormat) {
          return numeral(result).format(firstColumn.customFormat);
        } else if (firstColumn.displayFormat) {
          return numeral(result).format(firstColumn.displayFormat);
        } else {
          const fractionDigits = commons.isInteger(firstColumn.decimalPlaces) ? firstColumn.decimalPlaces : 2;
          return result.toLocaleString(undefined, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          });
        }
      }
      // money
      if (firstColumn.type == 4) {
        return numeral(result).format("0,0.00");
      }
      // date
      if (firstColumn.type == 5) {
        if (firstColumn.predefinedFormatId) {
          return luxon.fromISO(result).toFormat(commons.dateFormatTranslate(firstColumn.predefinedFormatId));
        } else {
          return luxon.fromISO(result).toFormat(commons.dateFormatTranslate("DD"));
        }
      }
      // date time
      if (firstColumn.type == 6) {
        if (firstColumn.predefinedFormatId) {
          return luxon.fromISO(result).toFormat(commons.dateFormatTranslate(firstColumn.predefinedFormatId));
        } else {
          return luxon.fromISO(result).toFormat(commons.dateFormatTranslate("DD t"));
        }
      }
      // location
      if (firstColumn.type == 9) {
        return normalize(result.placeName);
      }
      // link
      if (firstColumn.type == 33) {
        if (result.constructor === String) {
          return result;
        } else if (result.constructor == Object) {
          return normalize(result.name);
        }

        return null;
      }

      return null;
    },
    async executeActivity(activity, list, payload = {}) {
      const entries = Array.isArray(list) ? list : [];

      if (entries.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return false;
      }

      // An activity set to open a portal page opens it in a new tab instead (activityPortal.js):
      // from the click itself, before the unsaved-form question below awaits (the browser blocks a
      // tab opened after it), and leaving the open form and the row as they are.
      const portalId = activityPortalId(activity, this.header);
      if (openActivityPortalHere({ id: activity.id, portalId }, { moduleId: this.moduleId, entries, routeName: this.$route && this.$route.name })) {
        return true;
      }

      const formRef = this.$refs?.ftsListViewForm;
      const formOpened = formRef?.isShow ?? false;
      const formActivityId = formRef?.selectedActivity?.id ?? null;

      if (formOpened && formActivityId && !payload?.skipFormConfirmation) {
        const confirmation = await formRef.confirmUnsavedForm();
        if (!confirmation) {
          return false;
        }
      }

      let firstEntryTitle = null;
      if (entries.length > 0 && entries[0] && entries[0].id) {
        firstEntryTitle = this.getFormTitle(entries[0].id, activity.id);
      }

      if (typeof payload?.beforeOpen === "function") {
        payload.beforeOpen();
      }

      window.eventBus.$emit("listViewForm:open", {
        entries: entries,
        moduleId: this.moduleId,
        listingId: this.listingId,
        activityId: activity.id,
        activityName: activity.name,
        portalId: portalId,
        title: firstEntryTitle
      });

      return true;
    },
    executeAction(action, items) {
      commons.executeEntryCustom({
        action: action,
        items: items,
        modal: this.$refs.ftsActionPageModal
      });
    },
    async executeDocument(action, items) {
      if (items.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      let vm = this;
      if (action.type == "template") {
        let template = {
          templateId: action.id,
          entryId: items[0].id,
        };

        this.$refs.ftsDocumentModal.show(template);
      } else if (action.hasForm) {
        let items = this.resultSet.list.filter((x) => x.selected);
        await this.$refs.ftsDocumentFormModal.show({
          moduleId: this.moduleId,
          listingId: this.listingId,
          document: action,
          items: items,
          filters: this.filters
        });
      } else {
        let parameters = {
          document: action,
          items: items,
          moduleId: this.moduleId,
          listingId: this.listingId,
          filters: this.filters,
        };

        if (action.dataSourceId == "logicService") {
          if (!parameters.inputs) {
            parameters.inputs = {};
          }

          let items = this.resultSet.list.filter((x) => x.selected);
          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
            parameters.inputs.locusIds = items.map(x => x.id);
          }
        }

        window.eventBus.$emit("showPDFDocument", parameters);
      }
    },
    async openCreateForm() {
      await this.executeActivity({ id: "create" });
    },
    normalizeInlineEntryId(entryId) {
      return entryId == null ? "" : String(entryId);
    },
    getInlineAcknowledgedRevisions() {
      const revisions = {};

      Object.keys(this._inlineDrafts || {}).forEach(entryId => {
        revisions[entryId] = this._inlineDrafts[entryId].acknowledgedRevision;
      });

      return revisions;
    },
    applyInlineDraftOverlay(resultSet, acknowledgedAtRequest = {}) {
      if (!resultSet || !Array.isArray(resultSet.list)) return;

      resultSet.list.forEach(item => {
        const entryId = this.normalizeInlineEntryId(item.id);
        const draft = this._inlineDrafts && this._inlineDrafts[entryId];

        if (!draft) return;

        const acknowledgedAfterRequest =
          acknowledgedAtRequest[entryId] !== draft.acknowledgedRevision;

        if (draft.status === "clean" && !acknowledgedAfterRequest) return;

        item.extension = {
          ...(item.extension || {}),
          ..._.cloneDeep(draft.visibleSnapshot || {}),
        };
      });
    },
    removeInlineEntryFromQueue(entryId) {
      this._inlineQueue = (this._inlineQueue || []).filter(id => id !== entryId);
      if (this._inlineQueuedEntries) {
        delete this._inlineQueuedEntries[entryId];
      }
    },
    queueInlineDraft(draft) {
      const entryId = draft.entryId;
      if (this._inlineQueuedEntries[entryId]) return;

      this._inlineQueuedEntries[entryId] = true;
      this._inlineQueue.push(entryId);
    },
    scheduleInlineDrain() {
      clearTimeout(this._inlineSaveTimer);

      if (this._inlineDrainPromise) return;

      this._inlineSaveTimer = setTimeout(() => {
        this._inlineSaveTimer = null;
        this.drainInlineSaveQueue().catch(() => {});
      }, 1000);
    },
    handleInlineUpdate(payload = {}) {
      const { entryId, moduleId } = payload;
      const normalizedEntryId = this.normalizeInlineEntryId(entryId);

      if (
        !normalizedEntryId ||
        (moduleId != null && String(moduleId) !== String(this.moduleId))
      ) {
        return;
      }

      const patch = _.cloneDeep(payload.patch || payload.extension || {});
      let visibleSnapshot = _.cloneDeep(payload.visibleSnapshot || {});

      if (!Object.keys(visibleSnapshot).length) {
        const item = (this.resultSet.list || []).find(
          row => this.normalizeInlineEntryId(row.id) === normalizedEntryId
        );
        visibleSnapshot = _.cloneDeep((item && item.extension) || {});
      }

      let draft = this._inlineDrafts[normalizedEntryId];
      if (!draft) {
        draft = {
          entryId: normalizedEntryId,
          listingId: this.listingId,
          fullSnapshot: this.isDefaultListing ? _.cloneDeep(visibleSnapshot) : null,
          visibleSnapshot: {},
          baselinePromise: null,
          revision: 0,
          acknowledgedRevision: 0,
          status: "clean",
          error: null,
        };
        this._inlineDrafts[normalizedEntryId] = draft;
      }

      draft.visibleSnapshot = {
        ...draft.visibleSnapshot,
        ...visibleSnapshot,
        ...patch,
      };

      if (draft.fullSnapshot) {
        draft.fullSnapshot = {
          ...draft.fullSnapshot,
          ...visibleSnapshot,
          ...patch,
        };
      }

      draft.revision += 1;
      draft.status = "dirty";
      draft.error = null;

      this.queueInlineDraft(draft);
      this.scheduleInlineDrain();
    },
    async prepareInlineSnapshot(draft, generation) {
      if (draft.fullSnapshot) return draft.fullSnapshot;

      draft.status = "loading-baseline";

      if (!draft.baselinePromise) {
        draft.baselinePromise = classificationStore.dispatch("loadActivitySnapshot", {
          moduleId: this.moduleId ? this.moduleId.toString() : null,
          listingId: draft.listingId,
          activityId: "manage",
          entryId: draft.entryId,
          locusId: draft.entryId,
        });
      }

      let response;
      try {
        response = await draft.baselinePromise;
      } catch (error) {
        draft.baselinePromise = null;
        throw error;
      }

      if (generation !== this._inlineGeneration) return null;

      const baseline = response && response.classificationForm
        ? response.classificationForm.default
        : response;

      if (!baseline || typeof baseline !== "object") {
        draft.baselinePromise = null;
        throw new Error("Unable to load the complete inline entry snapshot.");
      }

      draft.fullSnapshot = {
        ..._.cloneDeep(baseline),
        ...draft.visibleSnapshot,
      };

      return draft.fullSnapshot;
    },
    async drainInlineSaveQueue() {
      if (this._inlineDrainPromise) return this._inlineDrainPromise;

      const generation = this._inlineGeneration;
      const moduleId = this.moduleId;

      this._inlineDrainPromise = (async () => {
        let firstError = null;

        while (
          generation === this._inlineGeneration &&
          this._inlineQueue.length
        ) {
          const entryId = this._inlineQueue.shift();
          delete this._inlineQueuedEntries[entryId];

          const draft = this._inlineDrafts[entryId];
          if (!draft || draft.acknowledgedRevision >= draft.revision) continue;

          try {
            const fullSnapshot = await this.prepareInlineSnapshot(draft, generation);
            if (
              !fullSnapshot ||
              generation !== this._inlineGeneration ||
              String(moduleId) !== String(this.moduleId)
            ) {
              continue;
            }

            const submittedRevision = draft.revision;
            const submittedSnapshot = _.cloneDeep(fullSnapshot);
            draft.status = "saving";

            await classificationStore.dispatch("submitActivityStateless", {
              moduleId: moduleId ? moduleId.toString() : null,
              activityId: "manage",
              payload: submittedSnapshot,
              entryIds: [draft.entryId],
            });

            if (
              generation !== this._inlineGeneration ||
              String(moduleId) !== String(this.moduleId)
            ) {
              continue;
            }

            draft.acknowledgedRevision = submittedRevision;
            draft.error = null;

            if (draft.revision > submittedRevision) {
              draft.status = "dirty";
              this.queueInlineDraft(draft);
            } else {
              draft.status = "clean";
            }
          } catch (error) {
            if (generation !== this._inlineGeneration) continue;

            draft.status = "failed";
            draft.error = error;
            this.removeInlineEntryFromQueue(entryId);
            firstError = firstError || error;

            this.$toasted.global.error({
              response: error && error.response,
              message: "Failed to save inline changes",
            });
          }
        }

        if (firstError) throw firstError;
      })().finally(() => {
        this._inlineDrainPromise = null;

        if (this._inlineQueue.length && !this._isBeingDestroyed && !this._isDestroyed) {
          this.scheduleInlineDrain();
        }
      });

      return this._inlineDrainPromise;
    },
    async flushInlineSaves() {
      clearTimeout(this._inlineSaveTimer);
      this._inlineSaveTimer = null;

      await this.drainInlineSaveQueue();

      const failedDraft = Object.values(this._inlineDrafts || {}).find(
        draft => draft.status === "failed"
      );
      if (failedDraft) throw failedDraft.error;
    },
    resetInlineSaveState() {
      clearTimeout(this._inlineSaveTimer);
      this._inlineSaveTimer = null;
      this._inlineGeneration += 1;
      this._inlineDrafts = {};
      this._inlineQueue = [];
      this._inlineQueuedEntries = {};
    },
    async addNewRowInline(){
      if (this._creatingInlineRow) return;
      this._creatingInlineRow = true;

      try {
        if (this.sort && this.sort.length > 0) {
          const confirmation = await this.$confirmation.show({
            title: "Would you like to remove sorting?",
            buttons: [
              {
                title: "Not remove",
                bind: { text: true }
              },
              {
                title: "Remove",
                default: true,
                handler: ({ resolve }) => resolve(true)
              }
            ]
          });
          if (confirmation !== true) return;
          this.sort = null;
        }

        await this.$nextTick();

        try {
          await this.flushInlineSaves();
        } catch (error) {
          return;
        }

        const res = await classificationStore.dispatch("submitActivity", {
          moduleId: this.moduleId,
          activityId: "create",
        });

        this._focusInlineEditEntryId = res.data.entryId;
        await this.retrieveItems();
      } catch (error) {
        this.$toasted.global.error({
          response: error && error.response,
          message: "Failed to create a new row",
        });
      } finally {
        this._creatingInlineRow = false;
      }
    },
    async onCreateFromEmptyView() {
      await this.designCreateField({
        displayName: "Text",
        type: 0
      });
    },
    setRow(row, value) {
      if (value === true && !this.bulk) {
        this.formData.forEach(x => {
          this.$set(x, "selected", false);
        });
      }

      this.$set(row, "selected", value);
    },
    async selectRow(row, payload) {
      if (commons.elementStopPropagation()) {
        return;
      }

      const isRowSelected = Boolean(row?.selected);

      if (payload?.skipForm) {
        this.setRow(row, !isRowSelected);
        return;
      }

      const formRef = this.$refs?.ftsListViewForm;
      const formOpened = formRef?.isShow ?? false;

      if (!formOpened) {
        this.setRow(row, !isRowSelected);
        return;
      }

      const formActivityId = formRef?.selectedActivity?.id ?? null;

      if (!formActivityId) {
        if (isRowSelected) {
          return;
        }

        this.setRow(row, true);
        this.executeActivity({ id: "quickView" }, [row]);
        return;
      }

      await this.executeActivity({ id: "quickView" }, [row], {
        beforeOpen: () => {
          if (!isRowSelected) {
            this.setRow(row, true);
          }
        }
      });
    },
    selectRowById(id, payload) {
      if (!Array.isArray(this.formData) || this.formData.length === 0) {
        return;
      }

      const row = this.formData.find(item => item.id === id);
      if (row) {
        this.selectRow(row, payload);
        this.$nextTick(() => {
          const firstSelectedRow = document.querySelector('.data-table__row--selected');
          if (firstSelectedRow) {
            firstSelectedRow.scrollIntoView({ block: 'center', inline: 'nearest' });
          }
        });
      }
    },
    async openViewForm(row) {
      await this.executeActivity({ id: "quickView" }, [row], {
        beforeOpen: () => this.setRow(row, true)
      });
    },
    openDesignWorkflowDrawer(args) {
      const {tab, action, data} = args;
      console.debug(args);

      if (this.$refs.ftsDesignWorkflowDrawer) {
        this.$refs.ftsDesignWorkflowDrawer.insertAt = args.insertAt;
        this.$refs.ftsDesignWorkflowDrawer.show();
      }

      if(tab){
        this.$refs.ftsDesignWorkflowDrawer.activeTab = tab;
      }

      if(tab == 'action' && action == 'create'){
        this.$refs.ftsDesignWorkflowDrawer.createAction();
        return;
      }

      if(tab == 'action' && action == 'edit' && data){
        var res = this.workflowData.actions.find((x)=>x.id == data.id);
        this.$refs.ftsDesignWorkflowDrawer.editAction(res);
        return;
      }

      if(tab == 'state' && action == 'create'){
        this.$refs.ftsDesignWorkflowDrawer.createState();
        return;
      }

      if(tab == 'action'){
        if(!this.$refs.ftsDesignWorkflowDrawer.actions.length){
           this.$refs.ftsDesignWorkflowDrawer.createAction();
           return;
        }
      }


      // if(defaultAction =='create'){
      //   if(initialTab =='state')
      //     this.$refs.ftsDesignWorkflowDrawer.createState();
      //   if(initialTab =='action')
      //     this.$refs.ftsDesignWorkflowDrawer.createAction();
      // } else if(defaultAction == 'edit'){
      //   if(initialTab =='action')
      //     this.$refs.ftsDesignWorkflowDrawer.editAction(data);
      // }
    },
    openDesignListingDrawer(args) {
      if (!this.$refs.ftsDesignListingDrawer) return;

      this.$refs.ftsDesignListingDrawer.show();
      if (args && args.carrier) {
        // The default view's own design (canvas.listDesign, R13 / §5.7).
        this.$refs.ftsDesignListingDrawer.editListDesign();
        return;
      }
      if (args && args.listingId) {
        this.$refs.ftsDesignListingDrawer.editListing(args.listingId)
        return;
      };

      if(args && args.new){
        this.$refs.ftsDesignListingDrawer.createListing();
      }
    },
    onInformationSortEnd() {
      // Get new order from the dragged array
      const newOrder = this.sortableInformation.filter((x)=> !x.system);
      // Clone canvas and update information order
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);

      if (this.hasCarrierColumns) {
        // The default view's designed columns live on canvas.listDesign (R5).
        canvasDesign.listDesignOf(canvas).list.columns = [...this.sortableInformation];
      } else if (this.isDefaultListing){
        const infoMap = new Map(
          canvas.information.map(info => [info.id, info])
        );

        canvas.information = newOrder
          .map(item => infoMap.get(item.id))
          .filter(Boolean);
      } else {
        let currentMenu = canvasDesign.getMenu(canvas, this.listingId);
        if (currentMenu) {
          currentMenu.list = currentMenu.list || { columns: [] };
          currentMenu.list.columns = Array.isArray(currentMenu.list.columns) ? currentMenu.list.columns : [];

          currentMenu.list.columns = [...this.sortableInformation];

        }
      }

      this.columns = [...this.sortableInformation];

      canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      this.onDesignChange();
    },
    async openColumnDrawer(column) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const res = await this.$refs.ftsInformationDesignDrawer.show(column);

      // Handle delete case
      if (res === true) {
        // Column was deleted via the drawer
        // The delete confirmation already handled the canvas update
        // Just sync local state

        if (canvas.menus) {
          canvas.menus.forEach(menu => {
            if (menu.list && menu.list.columns) {
              menu.list.columns = menu.list.columns.filter(
                col => col.id !== column.id
              );
            }
          });
        }

        // The page design (canvas.listDesign) is pruned the way a view is.
        if (canvas.listDesign) {
          ['list', 'mobileListDesign'].forEach(key => {
            const design = canvas.listDesign[key];
            if (design && Array.isArray(design.columns)) {
              design.columns = design.columns.filter(col => col.id !== column.id);
            }
          });
        }

        const infoIndex = canvas.information.findIndex(x => x.id === column.id);
        canvas.information.splice(infoIndex, 1);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);

        if (this.$route.query.l) {
          let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
          this.columns = [...currentMenu.list.columns];
        } else if (this.isMiniStudio || this.isDefaultListing) {
          const localColIndex = this.columns.findIndex(x => x.id === column.id);
          this.columns.splice(localColIndex, 1);
        }

        this.onDesignChange();
        return;
      }

      if (res && typeof res === 'object') {
        try {
          // Update canvas information with all properties from result
          const infoIndex = canvas.information.findIndex(x => x.id === res.id);
          if (infoIndex > -1) {
            // Replace the entire information object to capture all type-specific properties
            canvas.information[infoIndex] = res;
          }

          // Update menu columns
          if (!canvas.menus) canvas.menus = [];
          let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
          if (currentMenu && currentMenu.list && currentMenu.list.columns) {
            const colIndex = currentMenu.list.columns.findIndex(x => x.id === res.id);
            if (colIndex > -1) {
              currentMenu.list.columns[colIndex] = {
                ...currentMenu.list.columns[colIndex],
                displayName: res.displayName,
                type: res.type,
              };
            }
          }

          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.onDesignChange();

          // Update local columns
          const localColIndex = this.columns.findIndex(x => x.id === res.id);
          if (localColIndex > -1) {
            this.columns[localColIndex] = {
              ...this.columns[localColIndex],
              displayName: res.displayName,
              type: res.type,
            };

            // deep clone to force trigger columns update
            this.columns = [...this.columns];
          }

          // Sync sortableInformation
          this.sortableInformation = [...this.columns];
        } catch (err) {
          console.error("openColumnDrawer error:", err);
          this.$toasted.global.error();
        }
      }
    },
    // design
    async designSetModuleEmoji() {
      const designModuleName = (this.designModuleName || "").trim();
      const requestId = this.nextDesignAIEmojiRequestId();
      if (!designModuleName) return;

      try {
        const res = await canvasDesignStore.dispatch("ai", {
          type: "emoji",
          text: designModuleName
        });

        if (
          res.data &&
          requestId === this.designAIEmojiRequestId &&
          res.data !== this.designModuleEmoji
        ) {
          this.designModuleEmoji = res.data;
        }
      } catch (err) {
        console.error("Failed to generate design module emoji.", {
          name: designModuleName,
          moduleId: this.moduleId,
          listingId: this.listingId,
          error: err
        });
      }
    },
    designAddField(item, insertAt) {
      if (!item) return;

      if(item.type == 'state'){
        this.openDesignWorkflowDrawer({'tab':'state','action':'create','insertAt': insertAt});
        return;
      }

      try {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);

        // Ensure menus array exists before calling getMenu
        if (!canvas.menus) {
          canvas.menus = [];
        }

        // Add to current menu's columns
        let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
        if (currentMenu) {
          currentMenu.list = currentMenu.list || { columns: [] };
          currentMenu.list.columns = Array.isArray(currentMenu.list.columns) ? currentMenu.list.columns : [];

          if (this.columns && Array.isArray(this.columns)) {
            if(Number.isInteger(insertAt) && insertAt >= 0 && insertAt <= this.columns.length){
              currentMenu.list.columns.splice(insertAt, 0, item);
            }else {
            currentMenu.list.columns.push(item);
            }
          }
          this.columns = [...currentMenu.list.columns];
        }

        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        this.onDesignChange();
      } catch (err) {
        console.error("designAddField error:", err);
        this.$toasted.global.error();
      }
    },
    async designCreateField(item, insertAt) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      let res = canvasDesign.initInformation(item);

      // Check if this is an AI suggestion by matching against searchSuggestions
      const shortcut = this.$refs.ftsAddColumnShortcut;
      const isAiSuggestion = shortcut && shortcut.searchSuggestions && shortcut.searchSuggestions.some(
        s => s.value && s.value.id === item.type && s.value.name === item.displayName
      );

      const excludedType = [7, 20, 27];
      if(item && item.type && excludedType.includes(item.type) || !isAiSuggestion && item && typeof item === 'object' && item.type === undefined || item && !item.displayName){
        console.log('more information');
        // res = await this.$refs.ftsInformationDesignModal.show(res, null, null, { excludeTypes: [] });
        res = await this.$refs.ftsInformationDesignDrawer.show(res, null, null, {isCreate: true});
        if (!res || res === true) return;
      }

      /* Delete Case */
      // if (res === true) {
      //   this.sortableInformation = [...this.columns];
      //   const localColIndex = this.columns.findIndex(x => x.id === column.id);
      //   if (localColIndex > -1) {
      //     this.columns.splice(localColIndex, 1);
      //   }
      //   this.onDesignChange();
      //   return;
      // }

      if(!res || typeof res !== 'object') return;

      /* Proceed information creation Case */
      try {
        canvasDesign.setInformation(canvas, res);

        if (!canvas.menus) {
          canvas.menus = [];
        }
        let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
        if (currentMenu) {
          currentMenu.list = currentMenu.list || { columns: [] };
          currentMenu.list.columns = Array.isArray(currentMenu.list.columns) ? currentMenu.list.columns : [];

          if (this.columns && Array.isArray(this.columns)) {
            if(Number.isInteger(insertAt) && insertAt >= 0 && insertAt <= this.columns.length){
              currentMenu.list.columns.splice(insertAt, 0, res);
            }else {
            currentMenu.list.columns.push(res);
            }
          }
          this.columns = [...currentMenu.list.columns];

        } else {
          /* ministudio / without listing */
          this.columns.push(res);
        }

        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        this.onDesignChange();
        // this.sortableInformation = [...this.columns];
      } catch (err) {
        console.error(err);
        this.$toasted.global.error();
      }
    },
    onDesignChange() {
      this.$v.designModuleData.$touch();
      let saveData = _.cloneDeep(this.designModuleData);

      if (this.isDesignModuleNameError) {
        return;
      }

      this.autoSave.schedule(saveData);
    },
    // async designSaveNow() {
    //   await this.autoSave.flush();
    // },
    async designExecuteSave(payload) {
      const shouldReloadWorkspace = this.isDesignWorkspaceReloadRequired(payload);

      await canvasDesignStore.dispatch("saveVector", payload);

      return { shouldReloadWorkspace };
    },
    async designAfterSave({ shouldReloadWorkspace } = {}) {
      if (shouldReloadWorkspace) {
        // Reload workspace to sync sidebar structure after design/schema changes.
        await clientStore.dispatch("loadWorkspace", {
          id: clientStore.getters.workspaceId,
          forceReload: true,
          preserveMenuState: true
        });
      }

      // Notify views only after every pending schema revision is acknowledged.
      window.eventBus.$emit("listing:schemaUpdated");
    },
    isDesignWorkspaceReloadRequired(payload) {
      const originalVector = canvasDesignStore.state.originalVector;

      if (!payload || !originalVector) {
        return true;
      }

      const current = moduleGuard.compactModule(payload);
      const original = _.cloneDeep(originalVector);
      const labelFields = ["name", "emoji", "description"];

      labelFields.forEach(field => {
        delete current[field];
        delete original[field];
      });
      delete current.id;
      delete original.id;
      delete current.audit;
      delete original.audit;

      return !_.isEqual(current, original);
    },
    getDesignModuleField(field) {
      if (this.listingId && this.listingId !== "everything") {
        const canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const currentMenu = canvasDesign.getMenu(canvas, this.listingId);
        return currentMenu && currentMenu[field];
      }

      return canvasDesignStore.getters.vector && canvasDesignStore.getters.vector[field];
    },
    updateDesignHeaderField(field, value) {
      if (this.listingId && this.listingId !== "everything") {
        const canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const currentMenu = canvasDesign.getMenu(canvas, this.listingId);
        currentMenu[field] = value;
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);

        const shouldSyncClientListing =
          this.moduleId &&
          (
            field === "emoji" ||
            (field === "name" && !this.isDesignModuleNameError)
          );

        if (shouldSyncClientListing) {
          this.syncDesignLabelToClientStore(field, value);
        }

        this.onDesignChange();
        return;
      }

      canvasDesignStore.dispatch("updateModule", {
        ...canvasDesignStore.getters.vector,
        [field]: value
      });

      const shouldSyncClientModule =
        this.moduleId &&
        (
          field === "emoji" ||
          (field === "name" && !this.isDesignModuleNameError)
        );

      if (shouldSyncClientModule) {
        this.syncDesignLabelToClientStore(field, value);
      }

      this.onDesignChange();
    },
    syncDesignLabelToClientStore(field, value) {
      if (!this.moduleId) return;

      if (this.listingId && this.listingId !== "everything") {
        clientStore.dispatch("syncModuleListing", {
          moduleId: this.moduleId,
          listingId: this.listingId,
          data: {
            id: this.listingId,
            [field]: value
          }
        });
        return;
      }

      clientStore.dispatch("syncWorkspaceModule", {
        id: this.moduleId,
        [field]: value
      });
    },
    createAutoSave(saveFunction, delay = 1000) {
      let timer = null;
      let pending = null;
      let inFlight = null;
      let lastError = null;
      let requestedRevision = 0;
      let acknowledgedRevision = 0;
      let shouldReloadWorkspace = false;
      let afterSavePending = false;

      const reportError = err => {
        console.error("Auto-save failed:", err);
        this.$toasted.global.error({
          response: err && err.response,
          message: "Failed to save Studio changes",
        });
      };

      const runQueue = () => {
        if (inFlight) return inFlight;

        inFlight = (async () => {
          while (true) {
            let hasUnreportedAcknowledgement = false;

            while (pending) {
              const current = pending;
              pending = null;

              try {
                const result = await saveFunction(current.payload);
                acknowledgedRevision = current.revision;
                lastError = null;
                hasUnreportedAcknowledgement = true;
                afterSavePending = true;
                shouldReloadWorkspace = shouldReloadWorkspace || Boolean(
                  result && result.shouldReloadWorkspace
                );
              } catch (err) {
                if (!pending || pending.revision < current.revision) {
                  pending = current;
                }
                lastError = err;
                reportError(err);
                throw err;
              }
            }

            if (hasUnreportedAcknowledgement || afterSavePending) {
              try {
                await this.designAfterSave({ shouldReloadWorkspace });
                afterSavePending = false;
                shouldReloadWorkspace = false;
                lastError = null;
              } catch (err) {
                lastError = err;
                reportError(err);
                throw err;
              }
            }

            // A new local change may arrive while workspace/schema refresh is running.
            if (!pending) break;
          }
        })().finally(() => {
          inFlight = null;
        });

        return inFlight;
      };

      const triggerSave = () => {
        clearTimeout(timer);
        timer = null;
        return runQueue();
      };

      return {
        schedule(payload) {
          requestedRevision += 1;
          pending = {
            payload,
            revision: requestedRevision,
          };
          lastError = null;
          clearTimeout(timer);

          if (!inFlight) {
            timer = setTimeout(() => {
              triggerSave().catch(() => {});
            }, delay);
          }
        },
        async flush() {
          clearTimeout(timer);
          timer = null;
          lastError = null;
          await triggerSave();
        },
        cancel() {
          clearTimeout(timer);
          timer = null;
          pending = null;
          lastError = null;
        },
        getStatus() {
          return {
            inFlight: Boolean(inFlight),
            dirty: Boolean(pending) ||
              afterSavePending ||
              acknowledgedRevision < requestedRevision,
            requestedRevision,
            acknowledgedRevision,
            error: lastError,
          };
        },
      };
    },
    async cleanupAsyncWork({ flushAutoSave = false } = {}) {
      this.nextDesignAIEmojiRequestId();

      clearTimeout(this.moduleReadyResolveTimeout);
      this.moduleReadyResolveTimeout = null;

      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;

      if (this.debouncedFilters && this.debouncedFilters.cancel) {
        this.debouncedFilters.cancel();
      }

      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }

      if (this.autoSave) {
        if (flushAutoSave) {
          await this.autoSave.flush();
        }
        this.autoSave.cancel();
      }

      if (flushAutoSave) {
        await this.flushInlineSaves();
      }
    },
    async onOpenDesigner(args) {
      await this.moduleReadyPromise;
      this.openDesignListingDrawer(args);
    },
    startResize(e, index) {
      e.preventDefault();
      this.startX = e.clientX;
      this.startWidth = e.target.parentElement.offsetWidth;
      this.resizeIndex = index;

      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (this.resizeIndex === null) return;

      const delta = e.clientX - this.startX;
      const minWidth = 200;

      let newWidth = this.startWidth + delta;

      // 限制最小宽度
      if (newWidth < minWidth) {
        newWidth = minWidth;
      }

      const th = document.querySelectorAll("th")[this.resizeIndex];
      th.style.width = newWidth + "px";

      this.columns[this.resizeIndex].width = newWidth;
    },
    stopResize() {
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);

      /* find out resized information and trigger save of width */
      if (this.hasCarrierColumns) {
        // The default view's designed columns live on canvas.listDesign (R5).
        canvasDesign.listDesignOf(canvas).list.columns = [...this.columns];
      } else if (this.isMiniStudio || this.isDefaultListing) {
        const currentColumn = this.columns[this.resizeIndex];
        if (!currentColumn) return;

        const matchedInfo = canvas.information.find(
          info => info.id === currentColumn.id
        );

        if (matchedInfo) {
          matchedInfo.width = currentColumn.width;
        }
      } else {
        let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
        currentMenu.list.columns = [...this.columns];
      }
      canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      this.onDesignChange();

      this.resizeIndex = null;
    },
    columnWidth(column) {
      if(!column.width) return '200px';

      return column.width + 'px';
    },
    async changeState(entry, item, action) {
      if(action === 'create'){
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let state = canvasDesign.initState(item);
        canvasDesign.setState(canvas, state);
        canvasDesign.compactDesignBeforeSave(canvas);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        const res = await canvasDesignStore.dispatch("saveVector");
        clientStore.dispatch("setWorkspaceModuleStates", {
          moduleId: res.data.id,
          states: canvas.states
        });
        clientStore.dispatch("setDesignModeState", {
          success: true,
          updatedOn: res.data.updatedDate
        });
      }

      try {
        await classificationStore.dispatch("submitActivity", {
          moduleId: this.moduleId,
          activityId: "changeStatus",
          entryIds: [entry.id],
          state: item ? item.name : "",
        });
        this.refresh();
      } catch (error) {
        this.$toasted.error(error);
      }
    },
     async updateState(item) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, item.id);
        if (state) {
          canvasDesign.setState(canvas, {
            ...state,
            name: item.name,
            color: item.color
          });
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'state not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to update state'
        }); 
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async deleteState(id) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, id);
        if (state) {
          canvasDesign.deleteState(canvas, state);
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'state not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to delete state'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    onInformationHeader(column) {
     if (!this.sort || this.sort.length === 0) {
        this.sort = [{
          direction: 0,
          fieldId: column.id,
          id: commons.newUUID(),
        }];
        this.retrieveItems();
        return;
      }

      const currentSort = this.sort[0];

      if (currentSort.fieldId === column.id) {
        if (currentSort.direction === 0) {
          currentSort.direction = 1;
        } else {
          this.sort = null;
        }
      } else {
        this.sort = [{
          direction: 0,
          fieldId: column.id,
          id: commons.newUUID(),
        }];
      }
      this.retrieveItems();
    },
    isSortingHeader(column){
      if(!this.sort) return 'idle';

      var sortInfo = this.sort[0];

      if(sortInfo.fieldId !== column.id) return;

      if(sortInfo.direction === 0){
        return 'ascending';
      } else if(sortInfo.direction === 1){
        return 'descending';
      }

      return;
    },
    onEntryDelete(entry){
      if (!entry.selected) {
        this.setRow(entry, true);
      }
      
      this.executeActivity({id: 'delete'}, [entry]);
    },
    normalizeRef(ref) {
      return Array.isArray(ref) ? ref[0] : ref;
    },
    scheduleEmptyColumnHintPositionUpdate() {
      if (this.emptyColumnHintAnimationFrame) return;

      this.emptyColumnHintAnimationFrame = window.requestAnimationFrame(() => {
        this.emptyColumnHintAnimationFrame = null;
        this.updateEmptyColumnHintPosition();
      });
    },
    updateEmptyColumnHintPosition() {
      this.$nextTick(() => {
        if (!this.showEmptyColumnHint) {
          this.emptyColumnHintPosition = null;
          return;
        }

        const tableContainer = this.$refs.tableContainer;
        const listViewEmpty = this.$refs.listViewEmpty;
        const addColumnHeaderIcon = this.normalizeRef(this.$refs.addColumnHeaderIcon);
        const addColumnHeaderCell = this.normalizeRef(this.$refs.addColumnHeaderCell);
        const addColumnTarget = addColumnHeaderIcon || addColumnHeaderCell;

        if (!tableContainer || !addColumnTarget || !listViewEmpty) return;

        const containerRect = tableContainer.getBoundingClientRect();
        const targetRect = addColumnTarget.getBoundingClientRect();
        const emptyRect = listViewEmpty.getBoundingClientRect();
        const overlayLeft = tableContainer.scrollLeft;
        const overlayTop = tableContainer.scrollTop;
        const overlayWidth = tableContainer.clientWidth;
        const overlayHeight = Math.max(tableContainer.clientHeight, tableContainer.scrollHeight);
        const targetX = targetRect.left - containerRect.left + (targetRect.width / 2);
        const targetY = targetRect.bottom - containerRect.top + 32;
        const emptyTopY = emptyRect.top - containerRect.top;
        const emptyHeight = Math.max(emptyRect.height, 375);
        const emptyReferenceY = emptyTopY + (emptyHeight * 0.5);
        const emptyCenterX = emptyRect.left - containerRect.left + (emptyRect.width * 0.5);
        const hintPadding = 24;
        const textWidth = Math.min(128, Math.max(0, overlayWidth - (hintPadding * 2)));
        const textGap = 12;
        const startX = Math.min(
          Math.max(emptyCenterX + 140, targetX - 220),
          targetX - 120
        );
        const startY = emptyReferenceY;
        const controlOneX = startX + ((targetX - startX) * 0.46);
        const controlTwoX = startX + ((targetX - startX) * 0.82);
        const controlOneY = startY - 8;
        const controlTwoY = targetY + 92;
        const arrowSafeRight = Math.max(startX, controlOneX, controlTwoX, targetX - 96) + textGap;
        const maxTextLeft = overlayWidth - textWidth - hintPadding;
        const textLeft = Math.min(
          Math.max(hintPadding, arrowSafeRight),
          Math.max(hintPadding, maxTextLeft)
        );
        const textTop = startY - 10;

        this.emptyColumnHintPosition = {
          left: overlayLeft,
          top: overlayTop,
          width: overlayWidth,
          height: overlayHeight,
          textLeft,
          textTop,
          textMaxWidth: textWidth
        };
        this.emptyColumnHintViewBox = `0 0 ${overlayWidth} ${overlayHeight}`;
        this.emptyColumnHintPath = `M${startX} ${startY} C${controlOneX} ${controlOneY} ${controlTwoX} ${controlTwoY} ${targetX} ${targetY}`;
      });
    }
  },
  computed: {
    isModuleReady(){
        if (["lite", "lite-studio"].includes(this.$route.name)) {
          if (this.module && this.module.id) {
            return true;
          }
        }
        return false;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || this.workspaceBeta;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceBeta() {
      return clientStore.getters.workspaceBeta;
    },
    workspaceName() {
      return clientStore.getters.workspaceName;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceUsers() {
      return clientStore.getters.workspaceUsers;
    },
    module() {
      return clientStore.getters.module;
    },
    list() {
      return clientStore.getters.list;
    },
    states() {
      return clientStore.getters.moduleStates;
    },
    standardActivities() {
      return this.header && this.header.standards;
    },
    informations() {
      return canvasDesignStore.getters.canvasInformation || [];
    },
    isCreatable() {
      return this.isRightOf("create", this.standardActivities);
    },
    isQuickViewRight() {
      return this.isRightOf("quickView", this.standardActivities);
    },
    isViewRight() {
      return this.isRightOf("view", this.standardActivities);
    },
    isAssignRight() {
      return this.isRightOf("assign", this.standardActivities);
    },
    isCommentRight() {
      return this.isRightOf("comment", this.standardActivities);
    },
    isDeleteRight() {
      return this.isRightOf("delete", this.standardActivities);
    },
    isViewEmpty() {
      if (this.isDesignMode) {
        const columns = this.columns || [];
        const excludedIds = ['documentId', 'date', 'createdBy', 'lastChangeBy'];
        const hasVisibleColumns = columns.some(column => !excludedIds.includes(column.id));

        return !hasVisibleColumns && !this.resultSet?.list?.length;
      }

      return false;
    },
    showEmptyColumnHint() {
      return this.isViewEmpty && this.isDesignMode && this.isModuleReady;
    },
    emptyColumnHintStyle() {
      if (!this.emptyColumnHintPosition) return {};

      return {
        '--empty-column-hint-left': `${this.emptyColumnHintPosition.left}px`,
        '--empty-column-hint-top': `${this.emptyColumnHintPosition.top}px`,
        '--empty-column-hint-width': `${this.emptyColumnHintPosition.width}px`,
        '--empty-column-hint-height': `${this.emptyColumnHintPosition.height}px`,
        '--empty-column-hint-text-left': `${this.emptyColumnHintPosition.textLeft}px`,
        '--empty-column-hint-text-top': `${this.emptyColumnHintPosition.textTop}px`,
        '--empty-column-hint-text-max-width': `${this.emptyColumnHintPosition.textMaxWidth}px`,
      };
    },
    isTotalItemsExceed() {
      return this.totalItems >= this.totalItemslimit;
    },
    viewTitle() {
      if (this.list && this.list.id) {
        return {
          emoji: this.list.emoji,
          name: this.list.name
        }
      }

      if (this.module && this.module.id) {
        return {
          emoji: this.module.emoji,
          name: this.module.name
        }
      }

      return {
        name: "(undefined)"
      }
    },
    getColumns() {
      let columns = _.cloneDeep(this.columns);
      // exclude attachments
      columns = columns.filter(
        (col) => col.type !== 12 && col.type !== 13
      );
      // exclude hidden
      columns = columns.filter((col) =>
        (col.show === null || col.show === undefined || col.show === true) &&
        (col.hide === null || col.hide === undefined || col.hide !== true)
      );
      // set primary field
      if (columns && columns[0]) {
        columns[0].primaryField = true;
      }

      return columns;
    },
    filterColumns() {
      let filterColumns = this.composeFilterColumns(this.columns);
      // query selection suffix
      const querySelectionSuffix = queryLogic.querySelectionSuffix
        .filter(x => x.fieldName === "and" || x.fieldName === "or")
        .map(y => (({ ...y, system: true })));

      filterColumns = filterColumns.concat(querySelectionSuffix);

      return filterColumns;
    },
    totalItems() {
      return this.resultSet && this.resultSet.totalItems;
    },
    totalItemslimit() {
      const limit = this.resultSet && this.resultSet.limit;

      if (limit && commons.isInteger(limit) && limit > 0) {
        return limit;
      }

      return Listing_Max_Total_Entries;
    },
    activityList() {
      if (this.resultSet && this.header && this.header.activities) {
        const selectedItems = this.resultSet.list.filter((x) => x.selected);
        if (this.mode == "list" && selectedItems.length) {
          return this.header.activities.filter((activity) => {
            if (selectedItems.length > 1 && activity.disableBulkActions)
              return false;
            if (activity.fromStates && activity.fromStates.length) {
              return selectedItems.every((item) =>
                activity.fromStates.some((y) => y == entryStateId(item.state))
              );
            }
            return true;
          });
        }
      }
      return [];
    },
    actionList() {
      /* custom visibility */
      if (this.resultSet && this.header && this.header.customs) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.customs.filter((action) => {
          if (Boolean(action.condition)) {
            try {
              return Boolean(eval(action.condition));
            } catch (err) {
              console.error('[Custom:Condition error]', err);
              return false;
            }
          }

          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              return action.isMultiple;
            } else {
              return true;
            }
          }

          return action.isDefault;
        });
      }
      return [];
    },
    documentList() {
      if (this.resultSet && this.header && this.header.documents) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.documents.filter((action) => {
          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              // Multi-select: legacy allowSelection docs, PLUS htmljs single-entry ("entry type")
              // documents — those combine into ONE PDF across the selection (bulk print, §5d).
              // Collection/report docs (dataSourceId "collection") are whole-list, so stay excluded.
              return (
                action.allowSelection ||
                (action.engine === "htmljs" && action.dataSourceId !== "collection")
              );
            } else {
              return true;
            }
          }

          return (
            action.dataSourceId == "collection" ||
            action.dataSourceId == "logicService"
          );
        });
      }
      return [];
    },
    bulkActivities() {
      return [] // to-do
    },
    formData() {
      return this.resultSet.list ? this.resultSet.list : [];
    },
    currentSort() {
      if (Array.isArray(this.sort)) {
        const sort = this.sort[0];
        if (sort) {
          return `${sort.fieldId}:${sort.direction}`;
        }
      }

      return null;
    },
    isMiniStudio() {
      return this.$route.name == "lite-studio";
    },
    isDefaultListing() {
      return !this.listingId || this.listingId === "everything";
    },
    /* The default view (R13): no listing in the route and the module has no views — the module
       page everyone sees. Everything is never it (R14). */
    isDefaultView() {
      return !this.listingId && !canvasDesignStore.getters.canvasHasViews;
    },
    /* Derived until designed (R5): once the designer picked the page's columns they live on
       canvas.listDesign and reorder / resize edit them; before that the table shows every field
       in schema order and the same gestures edit the schema order, as they always did. */
    hasCarrierColumns() {
      const design = canvasDesignStore.getters.canvasListDesign;
      return !!(this.isDefaultView && design && design.list
        && Array.isArray(design.list.columns) && design.list.columns.length);
    },
    isDesignMode() {
      return clientStore.getters.liteDesignMode;
    },
    designModuleData() {
      return canvasDesignStore.getters.vector;
    },
    headerInfo() {
      if (this.isMiniStudio || this.isDefaultListing) {
        if (this.module) {
          let moduleData = {
            emoji: this.module.emoji,
            name: this.module.name,
            description: this.module.description
          }

          if (!moduleData.description) {
            moduleData.description = canvasDesignStore.getters.vector && canvasDesignStore.getters.vector.description;
          }

          return moduleData;
        }
      } else {
        if (this.list) {
          return {
            emoji: this.list.emoji,
            name: this.list.name,
            description: this.list.description
          }
        }
      }

      return {};
    },
    designModuleName: {
      get() {
        return this.getDesignModuleField("name");
      },
      set(val) {
        this.updateDesignHeaderField("name", val);
      }
    },
    designModuleEmoji: {
      get() {
        return this.getDesignModuleField("emoji");
      },
      set(val) {
        this.updateDesignHeaderField("emoji", val);
      }
    },
    designModuleDescription: {
      get() {
        return this.getDesignModuleField("description");
      },
      set(val) {
        this.updateDesignHeaderField("description", val);
      }
    },
    isDesignModuleNameError() {
      if (this.listingId && this.listingId !== 'everything') {
        if (
          this.designModuleData && 
          this.designModuleData.extension && 
          Array.isArray(this.designModuleData.extension.menus)
        ) {
          const menus = this.designModuleData.extension.menus;
          const index = menus.findIndex(menu => menu.id === this.listingId);
          return designValidator.menuDesignError(this.$v.designModuleData.extension.menus.$each[index], "name");
        }
        return false;
      }
      return designValidator.moduleDesignError(this.$v.designModuleData, "name");
    },
    designWorkspaceModules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    designSelectedInformation() {
      // The view's columns as the canvas holds them, not only this.columns: a field appended while an
      // earlier save is in flight is dropped from this.columns when that save's listing:schemaUpdated
      // reload lands, and must still count as selected (not re-offered under "Information").
      const selected = Array.isArray(this.sortableInformation) ? this.sortableInformation : [];
      const canvas = canvasDesignStore.state.canvas;
      const listingId = this.$route && this.$route.query && this.$route.query.l;
      const menu = canvas && Array.isArray(canvas.menus) && listingId
        ? canvasDesign.getMenu(canvas, listingId)
        : null;
      const canvasColumns = menu && menu.list && Array.isArray(menu.list.columns) ? menu.list.columns : [];
      const selectedKeys = new Set(selected.map(col => col.id));

      return [
        ...selected,
        ...canvasColumns.filter(col => col && !selectedKeys.has(col.id) && (!col.system || col.type === 'state'))
      ];
    },
    designModuleInformation() {
      const usedKeys = new Set(
        this.designSelectedInformation.map(col => col.id)
      )

      const available = this.informations.filter(
        info => !usedKeys.has(info.id)
      )

      const hasStateColumn = this.designSelectedInformation.some(
        col => col.id === 'state'
      )

      if (hasStateColumn) {
        return available
      }

      return [
        ...available,
        {
          id: 'state',
          displayName: 'State',
          system: true,
          fieldName: 'state',
          type: 'state',
        },
      ]
    },
    designModuleMenus() {
      return canvasDesignStore.getters.canvasMenus;
    },
    workflowData() {
      return {
        states: canvasDesignStore.getters.canvasStates,
        flows: canvasDesignStore.getters.canvasFlows,
        actions: canvasDesignStore.getters.canvasActivities,
        standardActions: canvasDesignStore.getters.canvasStandardActivities,
      };
    },
    listingData() {
      return {
        listing: canvasDesignStore.getters.canvasMenus,
        vectorName: canvasDesignStore.getters.vector.name,
      };
    }
  },
  watch: {
    filters: {
      deep: true,
      handler(val) {
        this.debouncedFilters.cancel();

        if (this.skipFiltersWatch) return;

        this.debouncedFilters(val);
      }
    },
    columns: {
      immediate: true,
      handler(val) {
        if (val && Array.isArray(val)) {
          this.sortableInformation = [...val.filter((x)=>(!x.system || x.type === 'state'))];
        }

        this.scheduleEmptyColumnHintPositionUpdate();
      }
    },
    showEmptyColumnHint() {
      this.scheduleEmptyColumnHintPositionUpdate();
    },
    contentElWidthStyle() {
      this.scheduleEmptyColumnHintPositionUpdate();
    },
    resultSet: {
      deep: true,
      handler() {
        this.scheduleEmptyColumnHintPositionUpdate();
      }
    }
  },
  created() {
    this.moduleReadyPromise = new Promise(resolve => {
      this.moduleReadyResolve = resolve;
    });
    this.refreshSchemaHandler = () => {
      this.refresh({ withHeader: true, withPreferenceFilters: true });
    }

    this.refreshListHandler = (payload = {}) => {
      if (!payload.moduleId || payload.moduleId == this.moduleId) {
        let option = {};
        if (payload.preserveSelectedEntries) {
          option.preserveSelectedEntries = true;
        }

        this.retrieveItems(option);
      }
    }

    this._inlineDrafts = {};
    this._inlineQueue = [];
    this._inlineQueuedEntries = {};
    this._inlineDrainPromise = null;
    this._inlineSaveTimer = null;
    this._inlineGeneration = 0;
    this._creatingInlineRow = false;
    this.inlineUpdateHandler = this.handleInlineUpdate;

    this.debouncedFilters = _.debounce(async (filters) => {
      this.page = 0;
      // preference
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)
      }
      this.setLocalStoragePreferenceFilters(preference, filters);
      // end preference
      await this.retrieveItems();
    }, 1000);

    this.debouncedSearch = _.debounce(async () => {
      this.page = 0;
      await this.retrieveItems();
    }, 400);

    this.autoSave = this.createAutoSave(this.designExecuteSave, 1000);

    window.eventBus.$on("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$on("listing:refresh", this.refreshListHandler);
    window.eventBus.$on("entry:updated", this.refreshListHandler);
    window.eventBus.$on("listing:inlineUpdated", this.inlineUpdateHandler);
  },
  async mounted() {
    window.eventBus.$on('open-designer', this.onOpenDesigner)
    window.eventBus.$on('lite:openDesignWorkflowDrawer', this.openDesignWorkflowDrawer)
    window.eventBus.$on('lite:switchListView', this.switchListView)

    this.isLeavingRoute = false;

    this.resizeObserver = new ResizeObserver(_.debounce((entries) => {
      try {
        const listViewContainer = this.$refs['list-view-container'];
        const entry = entries.find((resizeEntry) => resizeEntry.target === listViewContainer);

        if (!entry) return;

        const contentElWidth = Math.max(entry.contentRect.width, 768);

        this.contentElWidthStyle = {
          "position": "sticky",
          "left": "0",
          "width": `${contentElWidth}px`,
          "max-width": `${contentElWidth}px`,
        };
        this.scheduleEmptyColumnHintPositionUpdate();
      } catch (error) {
        console.warn("[DOMException]:ResizeObserver", error);
      }
    }, 100));
    this.resizeObserver.observe(this.$refs['list-view-container']);
    this.scheduleEmptyColumnHintPositionUpdate();
    window.addEventListener('resize', this.scheduleEmptyColumnHintPositionUpdate);

    const moduleId = this.$route?.params?.moduleId ? commons.decodeids(this.$route.params.moduleId) : null;

    if (!moduleId) {
      const routeName = this.$route.name;
      const isDesignAdmin = this.isDesignMode && this.isAdminUser;
      const hasWorkspaceModules = Boolean(this.workspaceModules && this.workspaceModules.length);
      const shouldOpenModuleHub = isDesignAdmin && (routeName === "lite-studio" || (routeName === "lite" && !hasWorkspaceModules));

      if (shouldOpenModuleHub) {
        this.redirectToModuleHub();
      } else {
        this.redirectToFirstAvailableModule("lite");
      }
      return;
    }

    this.moduleId = moduleId;
    await this.loadRouteModuleState(this.$route);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleEmptyColumnHintPositionUpdate);

    if (this.emptyColumnHintAnimationFrame) {
      window.cancelAnimationFrame(this.emptyColumnHintAnimationFrame);
      this.emptyColumnHintAnimationFrame = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs['list-view-container']);
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.cleanupAsyncWork({ flushAutoSave: true });

    // Clean up click outside listeners
    document.removeEventListener('click', this.handleColumnEditClickOutside);

    window.eventBus.$off("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$off("listing:refresh", this.refreshListHandler);
    window.eventBus.$off("entry:updated", this.refreshListHandler);
    window.eventBus.$off("listing:inlineUpdated", this.inlineUpdateHandler);
    window.eventBus.$off('open-designer', this.onOpenDesigner)
    window.eventBus.$off('lite:openDesignWorkflowDrawer', this.openDesignWorkflowDrawer)
    window.eventBus.$off('lite:switchListView', this.switchListView)

  },
  async beforeRouteLeave(to, from, next) {
    this.isLeavingRoute = true;
    try {
      await this.cleanupAsyncWork({ flushAutoSave: true });
      next();
    } catch (error) {
      this.isLeavingRoute = false;
      next(false);
    }
  },
  async beforeRouteUpdate(to, from, next) {
    const isSameModule = from.name == to.name && !changesets.diff(from.params, to.params).length;

    if (to.name === "lite-studio" && !isSameModule) {
      const moduleId = to?.params?.moduleId ? commons.decodeids(to.params.moduleId) : null;

      if (!moduleId) {
        next();
        if (this.isDesignMode && this.isAdminUser) {
          this.redirectToModuleHub();
        } else {
          this.redirectToFirstAvailableModule(to.name);
        }
        return;
      }

      try {
        await this.cleanupAsyncWork({ flushAutoSave: true });
      } catch (error) {
        next(false);
        return;
      }

      this.resetInlineSaveState();

      this.moduleReadyPromise = new Promise(resolve => {
        this.moduleReadyResolve = resolve;
      });
      this.isLeavingRoute = false;
      this.moduleId = moduleId;
      next();
      await this.loadRouteModuleState(to);
    } else {
      next();
    }

    if (to.fullPath !== from.fullPath) {
      if (!to.query.id && !to.query.a) {
        window.eventBus.$emit("list-view-form:close");
      } else {
        var toTrigger = false;

        if (to.query.id && (!from.query || to.query.id != from.query.id)) {
          toTrigger = true;
        } else if (
          to.query.a == "create" && !to.query.dialog &&
          (
            from.query.id ||
            Object.keys(to.query).some(x => x && x.startsWith('t') && x.length > 1)
          )
        ) {
          toTrigger = true;
        } else if (to.query.a == "create") {
          toTrigger = false;
        } else if (to.query.a && (!from.query || to.query.a != from.query.a)) {
          toTrigger = true;
        } else if (to.query.id && to.query.m !== from.query.m) {
          toTrigger = true;
        }

        if (toTrigger) {
          let entryId = null;
          let moduleId = null;

          let moduleUnspecified = false;

          if (to.query.id) {
            const queryDecodeids = commons.queryDecodeids(to.query.id);
            if (queryDecodeids && queryDecodeids.entryId) {
              entryId = queryDecodeids.entryId;
            }
            if (queryDecodeids && queryDecodeids.moduleId) {
              moduleId = queryDecodeids.moduleId;
            }
            moduleUnspecified = Boolean(queryDecodeids && queryDecodeids.moduleUnspecified);
            /* Only a deprecated (unprefixed) id implies "the module of this route". An id that
               explicitly encodes entry-only came from a wildcard connection, whose entry lives in
               another module — substituting this route's module makes the form request fail with
               "Module not match with entry". Leave it null and let the server resolve it (SS05672). */
            if (!moduleId && !moduleUnspecified && to.params.moduleId) {
              moduleId = commons.decodeids(to.params.moduleId);
            }
          } else if (to.query.a == "create" && to.params.moduleId) {
            moduleId = commons.decodeids(to.params.moduleId);
          }

          window.eventBus.$emit("listViewForm:open", {
            id: entryId,
            moduleId: moduleId,
            /* A listing belongs to this route's module; it cannot authorize an entry the server
               is about to resolve into a different one. */
            listingId: moduleUnspecified ? null : to.query.l,
            activityId: to.query.a,
            title: this.getFormTitle(entryId, to.query.a),
            fromUrl: true,
            callback: () => {
              this.selectRowById(entryId, { skipForm: true });
            }
          });
        }
      }
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

// Calm density P1 (2026-09-05): the listing no longer paints the shell's content area
// neutral-50 — that rule was unscoped and leaked into every Lite page. The page is white.

.layout-position-sticky-left {
  position: sticky;
  left: 0;
}

.fts-list-view-2 {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.list-view__container {
  flex: 1;
  padding: 1rem;
}

.list-view__header {
  @extend .layout-position-sticky-left;
  display: flex;
  align-items: flex-start;
}
.list-view__header-emoji {
  flex-shrink: 0;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  border-radius: var(--inistate-rounded-8);
  background-color: var(--inistate-color-primary-50);
}
.list-view__header-info {
  min-width: 0;
  margin-top: 0.125rem;
}
.list-view__header-name {
  font-weight: var(--inistate-font-weight-bold);
  font-size: var(--inistate-heading-text-24);
  line-height: var(--inistate-heading-text-24--line-height);
  color: var(--inistate-text-primary);

  &:not(.list-view__header-name--editable) {
    @include text-overflow-ellipsis;
  }
}
.list-view__header-description {
  font-size: var(--inistate-label-text-16);
  line-height: var(--inistate-label-text-16--line-height);
  color: var(--inistate-text-secondary);

  &:not(.list-view__header-description--editable) {
    @include text-overflow-ellipsis;
  }
}
.list-view__header-action {
  flex-shrink: 0;
  height: var(--inistate-heading-text-24--line-height);
  width: var(--inistate-heading-text-24--line-height);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  font-size: var(--inistate-label-text-16);
  color: var(--inistate-color-neutral-500);
  border-radius: 10em;
  background-color: transparent;
  cursor: pointer;
  > .tippy-trigger {
    cursor: inherit;
  }
  &:hover {
    background-color: var(--fts-gray-50);
  }
}
.list-view__header-error-message {
  margin-top: 0.25rem;
  color: var(--fts-danger);
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-label-text-14--line-height);
}

.list-view__content {
  display: inline-flex;
  flex-direction: column;
  min-width: 100%;
  padding-top: 32px;
}

.list-view__content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  overflow-x: auto;
}

.list-view__content-header__left {
  flex: 1;
  align-items: center;
  width: 100%;

  @include flex-gap($gap: 0.5rem, $display: flex);

  .activities-design-toggle {
    &.fts-btn.fts-btn-outline:not(.disabled) {
      border: 2px dashed var(--fts-gray-300);
      > span {
        font-weight: 400;
      }
    }
  }
  .activities-design-toggle-create {
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--fts-default);
    width: 20px;
    height: 20px;
    border-radius: 99px;
    font-size: 14px;

    cursor: pointer;
    &:hover{
      background-color: var(--fts-gray-300);
      background-color: var(--fts-gray-50);
    }
  }

  .btn-activity {
    background-color: #fff !important;
  }
}

.list-view__content-header__right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.list-view__content-divider {
  margin-bottom: 1rem;
  border-top: 1px solid var(--fts-border);
}

.list-view__content-filter {
  margin-bottom: 1rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
}

.list-view__content-pagination {
  margin-top: 1rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
}

.list-view__error {
  padding: 1rem;
  text-align: center;
}
.list-view__error-title {
  margin-bottom: 0.375rem;
  font-weight: var(--inistate-font-weight-medium);
  font-size: var(--inistate-paragraph-text-16);
  line-height: var(--inistate-paragraph-text-16--line-height);
  color: var(--fts-danger);
}
.list-view__error-message {
  font-size: var(--inistate-paragraph-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  color: var(--fts-default);
}

.list-view-empty {
  width: 375px;
  max-width: 100%;
  min-height: 375px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  padding-top: 32px;
  padding-bottom: 24px;

  &__icon {
    max-width: 100%;
    width: 160px;
    margin-bottom: 12px;
    margin-left: auto;
    margin-right: auto;
    > img {
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      margin: auto;
    }
  }

  &__title {
    margin-bottom: 6px;
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-primary);
  }

  &__description {
    margin-bottom: 16px;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    color: var(--inistate-text-secondary);
  }
}

.list-view-empty-column-hint {
  position: absolute;
  left: var(--empty-column-hint-left, 0px);
  top: var(--empty-column-hint-top, 0px);
  width: var(--empty-column-hint-width, 100%);
  height: var(--empty-column-hint-height, 100%);
  overflow: hidden;
  pointer-events: none;
  color: var(--inistate-color-primary-500);
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-label-text-14--line-height);
  z-index: 2;

  svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__curve {
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-dasharray: 4 6;
    stroke-linecap: round;
    marker-end: url(#list-view-empty-column-hint-arrowhead);
  }

  &__mask {
    fill: #fff;
    transform-origin: left center;
    animation: list-view-empty-column-hint-reveal 280ms ease-out both;
  }

  &__head {
    fill: currentColor;
    opacity: 0;
    animation: list-view-empty-column-hint-head 60ms ease-out 250ms both;
  }

  &__text {
    position: absolute;
    left: var(--empty-column-hint-text-left, 0px);
    top: var(--empty-column-hint-text-top, 0px);
    max-width: var(--empty-column-hint-text-max-width, 100%);
    padding: 0 4px;
    background-color: #fff;
    border-radius: var(--inistate-rounded-4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0;
    animation: list-view-empty-column-hint-text 90ms ease-out 210ms both;
  }
}

@keyframes list-view-empty-column-hint-reveal {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

@keyframes list-view-empty-column-hint-head {
  to {
    opacity: 1;
  }
}

@keyframes list-view-empty-column-hint-text {
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .list-view-empty-column-hint__mask,
  .list-view-empty-column-hint__head,
  .list-view-empty-column-hint__text {
    animation: none;
  }

  .list-view-empty-column-hint__head,
  .list-view-empty-column-hint__text {
    opacity: 1;
  }
}

.table-outter{
  padding: 16px;
  border: 1px solid var(--inistate-border-primary);
  background-color: #fff;
  border-radius: var(--inistate-rounded-8);
}

.table-container{
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
}

.fts-data-table {
  table-layout: fixed;
  width: 100%;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0px;
  

  .data-table__head {
    .data-table__row {
      background-color: var(--inistate-color-neutral-50);
    }

    .data-table__cell {
      color: var(--inistate-text-secondary);
      white-space: nowrap;
      vertical-align: middle;
    }

    .data-table__label-group {
      align-items: center;
    }

    .data-table__cell-placeholder {
      background-color: var(--fts-gray-50);
    }
  }

  .data-table__body {
    .data-table__row {
      border-bottom: 1px solid var(--fts-border);
    }
  }

  .data-table__row {
    &:hover {
      background-color: var(--inistate-color-neutral-50);

      .data-table__cell-shortcut {
        background-color: var(--inistate-color-neutral-50);
      }

      .shortcut-container {
        display: flex; 
      }
    }

    &.data-table__row--selected {
      background-color: var(--inistate-color-primary-50);

      &:hover {
        background-color: var(--inistate-color-primary-50);
      }

      .data-table__cell-shortcut {
        background-color: var(--inistate-color-primary-50);
      }
    }
  }

  .data-table__cell {
    font-weight: normal;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    text-align: left;
    vertical-align: top;
    color: var(--inistate-text-primary);
    white-space: nowrap;
    position: relative;
    padding: 10px 0px 10px 40px;
    border-bottom: 0.1px solid var(--fts-border);

    &.data-table__cell--inline-header{
      // background-color: red;
      padding: 0px;
      background-color: #fff;
    }

    &.data-table__cell--primary-cell {
      position: relative;
      height: 38px; /* fixed header height */
    }
    &.data-table__cell--design {
      cursor: pointer;
      &:hover {
        .data-table__cell-design-delete {
          visibility: visible;
        }
      }
    }
    &.data-table__cell--design-control {
      min-width: 200px;
    }
    &.data-table__cell--header {
      background-color: var(--fts-gray-50);
      border-bottom: 1px solid var(--fts-border);
      color: var(--inistate-text-primary);
      font-weight: var(--inistate-font-weight-medium);
      height: 40px;

      &:not(.data-table__cell--design) {
        padding-right: 44px;
      }

      &:hover:not(.other-col-resizing) {
        background-color: var(--inistate-color-neutral-100);

        .actions-group {
          display: flex;
        }

        &:has(.resize-handle) {
          .resize-handle {
            opacity: 1;
          }
        }
      }

      &:hover {
        .data-table__handle {
          display: block;
        }
      }

      .resize-handle {
        position: absolute;
        right: 0;
        top: 0;
        height: 100%;
        cursor: col-resize;
        background-color: var(--fts-primary);
        width: 6px;
        opacity: 0;
        // &:hover{
        //   opacity: 1;
        // }
      }
      &:has(.resize-handle-dragging) {
        .resize-handle {
          opacity: 1;
        }
        background: transparent;
        .actions-group{
          display: none;
        }
      }

      .label-text {
      display: inline-block;  // text shrinks to its own width
      }
      .actions-group{
        display: none;
        color: var(--fts-default);

        .actions-group-action{
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          line-height: 1;
          font-size: 12px;

          &:hover {
            background-color: var(--fts-gray-200);
          }
        }
      }
    }
  }

  .data-table_cell__handle {
    position: absolute;
    left: 14px;
    top: 8px;
    height: 24px;
    width: 24px;
    color: var(--fts-default);
    font-size: 12px;
    visibility: hidden;
    cursor: grab;

    display: flex;
    align-items: center;
    justify-content: center;

    .handle-icon {
      font-size: 12px;
    }
  }

  .data-table__cell-design-delete {
    margin-top: -4px;
    margin-bottom: -4px;
    margin-left: 4px;
    visibility: hidden;
  }

  .data-table__cell-placeholder {
    flex: 1 1 auto;
    min-width: 0;
  }

  .data-table__cell-action__sticky {
    position: sticky;
    right: 0;
    background: white;
    // border-right: 20px solid white;
    border-left: 0.1px solid var(--fts-border);
    border-bottom: 1px solid var(--fts-border);
    box-shadow: -2px 0 6px -4px rgba(0, 0, 0, 0.25);
    padding: 8px 0px 0px 12px;
    min-width: 100px;
    width: 100px;

    &--header {
      padding: 0px 0px 0px 12px;
      border-right: 0px;

      background-color: var(--fts-gray-50) !important;
    }
    
    .action-icon {
      flex-wrap: wrap;
      width: 24px;
      height: 24px;
      font-size: 12px;
      color: var(--fts-default);
      align-content: center;
      justify-content: center;
      display: flex;
      cursor: pointer;
      border-radius: 4px;

      &:hover {
        background-color: rgba(var(--fts-gray-100-rgb), 0.75);
      }
    }
  }

  .data-table__handle {
    font-size: 12px;
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--fts-default);
    font-size: 0.75rem;
    display: none;
    cursor: grab;
  }

  .data-table__cell-state {
    display: flex;
  }

  /* SS05909: why a searched row came back, when the field it matched on is not a column here.
     Quiet by design - it explains a row, it does not compete with its content. */
  .search-match-hint {
    margin-top: 2px;
    font-size: var(--inistate-label-text-12);
    line-height: var(--inistate-label-text-12--line-height);
    color: var(--inistate-text-secondary);
  }

  .data-table__cell-shortcut {
    position: absolute;
    top: 6px;
    right: -20px;
    font-size: 12px;
    display: flex !important; 
    gap: 6px;
    padding-left: 6px;
    padding-right: 6px;
    color: var(--fts-default);
    height: 28px;
    z-index: 99;
    background-color: var(--inistate-color-shade-white);
    color: var(--inistate-text-secondary);

    .shortcut-container {
      display: none;
      align-items: center;
      justify-content: center;
      border-radius: var(--inistate-rounded-4);

      .shortcut-container-icon {
        font-weight: normal;
        font-size: 16px;
        color: var(--inistate-icon-secondary);
      }
    }

    .shortcut-container-comment {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: var(--inistate-label-text-12);
      line-height: var(--inistate-label-text-12--line-height);
      cursor: default;
    }

    .shortcut-container-intention {
      display: flex;
      cursor: default;
    }

    .shortcut-container-btn {
      gap: 4px;
      padding-left: 4px;
      padding-right: 4px;
      background-color: var(--inistate-color-shade-white);
      font-size: var(--inistate-paragraph-text-12);
      line-height: var(--inistate-paragraph-text-12--line-height);
      cursor: pointer;
    }

    .shortcut-container-btn-icon {
      height: 16px;
      width: 16px;

      &:hover {
        color: var(--inistate-text-primary);
      }
    }

    .fts-standard-menu {
      .dropdown-select-toggle {
        .standard-menu-toggle {
          color: var(--inistate-icon-secondary);
          font-size: 14px;
        }
      }
    }
  }

  .data-table__label-group {
    display: flex;
    align-items: flex-start;
  }

  .fts-label-group.data-table__label {
    .label-group__icon {
      color: var(--inistate-icon-primary);
    }

    .label-group__label {
      color: var(--inistate-text-primary);
    }
  }

  .data-table__status {
    >div {
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-paragraph-text-14--line-height);
    }
  }

  .data-table__sort {
    font-size: 10px;
    color: var(--inistate-text-primary);
    display: flex;
    align-content: center;
    justify-content: center;
    cursor: pointer;

    &:not(:first-child) {
      margin-left: 6px;
    }

    &:not(:last-child) {
      margin-right: 6px;
    }

    &--ghost {
      color: var(--fts-default);

      &:hover {
        color: var(--inistate-text-primary);
      }
    }
  }

  .data-table__header{
    display: flex;
    align-items: center;
    padding-top: 4px;
    padding-bottom: 4px;
  }
}

// design
.lv-design__header {
  @extend .layout-position-sticky-left;
  display: flex;
  align-items: flex-start;
  padding-bottom: 0px;
  border-radius: var(--inistate-rounded-8);
  background-color: #fff;
}

.lv-design__header-wrapper {
  min-width: 0;
  height: 100%;
  align-self: center;
}

.lv-design__header-action {
  margin-top: 0.1rem;
  margin-left: 0.5rem;
  color: var(--fts-default);
  padding: 5px;
  border-radius: 99px;
  cursor: pointer;
  
  &:hover {
    background-color: var(--fts-gray-300);
    background-color: var(--fts-gray-50);
  }
}

.lv-design__header-info {
  margin-top: 5px;
  margin-bottom: 5px;

  >*:not(:last-child) {
    margin-bottom: 2px;
  }
}

.lv-design__module-emoji {
  flex-shrink: 0;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  border-radius: var(--inistate-rounded-8);
  background-color: var(--inistate-color-primary-50);
}

.lv-design__module-name {
  margin-bottom: 4px;
  font-weight: var(--inistate-font-weight-bold);
  font-size: var(--inistate-heading-text-18);
  line-height: var(--inistate-heading-text-18--line-height);
}

.lv-design__module-description {
  font-size: var(--inistate-label-text-16);
  line-height: var(--inistate-label-text-16--line-height);
}

.lv-design__error-message {
  color: var(--fts-danger);
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-label-text-14--line-height);
}

// Information Sort Dropdown
.information-sort-dropdown {
  min-width: 280px;
  max-width: 320px;
  max-height: 450px;
  overflow-y: auto;
}

.information-sort-menu {
  padding: 0.25rem 0;
}

.information-sort-header {
  padding: 0.5rem 0.75rem;
  font-weight: var(--inistate-font-weight-bold);
  font-size: var(--inistate-label-text-14);
  color: var(--inistate-text-secondary);
  border-bottom: 1px solid var(--fts-border);
  margin-bottom: 0.25rem;
}

.information-sort-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin: 0 0.25rem;
  border-radius: 0.25rem;
  cursor: default;

  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.75);

    .information-sort-handle {
      visibility: visible;
    }
  }
}

.information-sort-handle {
  color: var(--fts-default);
  font-size: 0.75rem;
  visibility: hidden;
  cursor: grab;
}

.information-sort-icon {
  color: var(--inistate-text-secondary);
  font-size: 0.875rem;
  width: 1rem;
  text-align: center;
}

.information-sort-name {
  flex: 1;
  font-size: var(--inistate-label-text-14);
  color: var(--inistate-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.information-sort-item.ghost {
  opacity: 0.5;
  background: var(--fts-background-secondary);
}

.add-column-trigger {
  cursor: pointer;
  color: var(--inistate-text-secondary);

  &:hover {
    color: var(--inistate-text);
  }

  // Align the plus icon with other column icons
  >div:first-child {
    width: 1rem;
    text-align: center;
  }
}

.add-column-edit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.add-column-input {
  flex: 1;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--fts-border);
  border-radius: var(--inistate-rounded-4);
  font-size: var(--inistate-label-text-14);
  background: var(--fts-background);
  color: var(--inistate-text-primary);
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: var(--inistate-color-primary);
  }

  &::placeholder {
    color: var(--fts-disabled);
  }
}

.add-column-type-trigger {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--fts-border);
  border-radius: var(--inistate-rounded-4);
  cursor: pointer;
  font-size: var(--inistate-label-text-14);
  color: var(--inistate-text-secondary);
  background: var(--fts-background);
  white-space: nowrap;

  &:hover {
    border-color: var(--inistate-color-primary);
    color: var(--inistate-text-primary);
  }
}

.btn-activity{
  
  .btn-activity-action {
    display: none;
    position: relative;
    color: var(--fts-default);
    width: inherit;
    right: -8px;
    width: 12px;
  }
  &:hover > .btn-activity-action {
    display: flex;
  }
}
.view-bar {
  display: flex;
  align-items: top;
  width: 100%;
  flex-wrap: nowrap;
  margin-left: 0px;
  margin-right: 0px;
  border-bottom: 1px solid var(--fts-border);
}

.view-tabs-scroll {
  min-width: 0;
  overflow-x: auto;
  white-space: nowrap;
}

/* hide scrollbar */
.view-tabs-scroll::-webkit-scrollbar {
  display: none;
}
.view-tabs-scroll {
  scrollbar-width: none;
}

.view-tabs {
  display: inline-flex;
  gap: 8px;
}

/* Right-side actions */
.view-tab-actions {
  --actions-width: 80px;
  width: var(--actions-width);
  flex-shrink: 0;

  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 0.1rem;
}

.view-tab-action {
  cursor: pointer;
  font-size: 16px;
}
.view-tab{
  display: flex;
  gap: 6px;
  padding: 0px 15px;
  padding-bottom: 10px;
  &:hover{
    cursor: pointer;
  }
}
.view-tab__active{
  color: var(--fts-primary);
  font-weight: 500;
  border-bottom: var(--fts-primary) 4px solid;
}
.view-tab-action{
  padding: 0px 10px;
  color: var(--fts-default);
  &:hover{
    cursor: pointer;
  }
}
/*  */
.list-view__content-secondary-header{
  position: relative;
  display: flex;
  // background-color: aqua;
  justify-content: space-between;
  margin-bottom: 1rem;

}
.list-view__content-secondary-header-actions{
  position: relative;
  display: flex;
  gap: 10px;
}
/* search input */
.table-search {
  position: relative;
  display: flex;

  .fts-form-control{
    border-radius: 4px;
  }

  .table-search-control {
    padding-right: 2rem;
  }
  .table-search-action {
    @include absolute-center-y;
    right: 0.75rem;
    font-size: 0.75rem;
    color: var(--inistate-icon-primary);
    &.ltable-search-action--search {
      pointer-events: none;
    }
    &.ltable-search-action--times {
      cursor: pointer;
    }
  }
}

.divider {
  height: 1px;
  background-color: var(--fts-border);
  margin-bottom: 20px;

}

.filter-action-btn__active {
  background-color: rgb(var(--fts-primary-rgb), 0.1 ) !important;
  color: var(--fts-primary) !important;
  border: 1px solid var(--fts-primary) !important;

  .filter-count-badge {
    background-color:  var(--inistate-color-primary-500);
    margin-left: 10px;
    border-radius: 99px;
    width: 20px;
    height: 20px;
    color: var(--inistate-color-shade-white);
  }
}

.fts-list-view-display {

  &.fts-scrollbar {
    overflow: hidden !important;
  }
}

@media (max-width: 768px) {
  .list-view-empty {
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;
  }
}

/* ==========================================================================
   Calm density — P1 (2026-09-05)
   docs/frontend-lite-listing-calm-density-proposal.md, rulings R1–R16 (recommended options).
   Class-level only: nothing added, removed or re-parented. Every rule below overrides one
   above by specificity, so reverting a ruling = deleting its block. Numbers are the ledger
   scale shared with the form designer: 13px rows, 12px labels, hairlines gray-100/200,
   one accent (New / Save), weight-not-hue for "current".
   ========================================================================== */
.fts-list-view-2 {

  /* ---- R1 · header: one 28px line, emoji inline, no tile ---- */
  .list-view__header {
    align-items: baseline;
    min-height: 28px;
    gap: 12px;
    &:hover .list-view__header-action { opacity: 1; }
  }
  .list-view__header-emoji {
    height: 28px;
    width: auto;
    margin-right: 0;
    border-radius: 0;
    background-color: transparent;
    align-self: center;
  }
  .list-view__header-info {
    flex: 1;
    min-width: 0;
    margin-top: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 12px;
  }
  .list-view__header-name {
    flex-shrink: 0;
    max-width: 60%;
    font-size: 1.125rem;
    line-height: 28px;
    font-weight: 600;
    /* design mode: the contenteditable defaults to width:100%, which would stretch the name to
       its 60% cap and push the description two-thirds across the line — shrink to content */
    &.list-view__header-name--editable {
      width: auto;
      min-width: 6ch;
    }
  }
  .list-view__header-description {
    flex: 1;
    min-width: 0;
    font-size: 0.8125rem;
    line-height: 20px;
    &.list-view__header-description--editable { width: auto; }
  }
  .list-view__header-error-message { flex-basis: 100%; margin-top: 0; }
  .list-view__header-action {
    height: 24px;
    width: 24px;
    margin-left: 0;
    align-self: center;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.15s ease, background-color 0.15s ease;
  }
  .list-view__content { padding-top: 12px; }

  /* ---- R2 / R3 · toolbar: one 32px row, New is the only accent ---- */
  .list-view__content > div:has(> .list-view__content-header) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 12px;
    row-gap: 12px;
    margin-bottom: 12px;
  }
  .list-view__content-header {
    flex: 1 1 auto;
    min-width: 0;
    margin-bottom: 0;
    /* design mode: the view-tab bar takes the whole first line; query tools wrap under it */
    &:has(> .list-view__content-header__left > .view-bar) { flex-basis: 100%; }
  }
  .list-view__content > div > .divider { display: none; }
  .list-view__content-secondary-header {
    flex: 0 0 auto;
    margin-left: auto;
    margin-bottom: 0;
  }
  .list-view__content-secondary-header-actions { gap: 2px; }
  .list-view__content-filter {
    flex-basis: 100%;
    margin: -4px 0 0;
    font-size: 0.8125rem;
  }

  .list-view__content-header__left {
    .fts-btn {
      height: 32px;
      min-height: 32px;
      padding: 0 12px;
      font-size: 0.8125rem;
      letter-spacing: 0;
    }
    /* R2a — activities / documents / actions / bulk stay where they are, as ghosts:
       no outline, no per-activity hue; the emoji is the identity. Dim until a row is selected. */
    .btn-activity {
      padding: 0 10px;
      font-weight: 500;
      background-color: transparent !important;
      border-color: transparent !important;
      color: var(--fts-default) !important;
      transition: color 0.15s ease, background-color 0.15s ease;
      &:hover {
        background-color: var(--inistate-color-neutral-50) !important;
        color: var(--fts-text) !important;
      }
      .btn-activity-icon,
      .btn-activity-label { color: inherit; }
    }
  }
  &:has(.data-table__row--selected) .list-view__content-header__left .btn-activity {
    color: var(--fts-text) !important;
  }

  .table-search .fts-form-control {
    height: 32px;
    min-height: 32px;
    width: 220px;
    padding: 0 30px 0 10px;
    font-size: 0.8125rem;
    border-color: var(--fts-gray-200);
  }
  .table-search .table-search-action {
    right: 10px;
    font-size: 14px;
    color: var(--fts-default);
  }

  /* R3 — Filter / Sort / Refresh as text-weight toggles; active = weight + digits, no pill */
  .filter-action-button,
  .sort-action-button {
    height: 32px !important;
    min-height: 32px;
    padding: 0 8px !important;
    border-color: transparent !important;
    background-color: transparent !important;
    color: var(--inistate-text-secondary) !important;
    font-size: 0.8125rem;
    font-weight: 400;
    letter-spacing: 0;
    &:hover {
      background-color: var(--inistate-color-neutral-50) !important;
      color: var(--fts-text) !important;
    }
    .filter-action-button-icon,
    .sort-action-button-icon {
      font-weight: 400;
      color: var(--fts-default);
    }
    &.filter-action-btn--active,
    &.sort-action-button--active {
      color: var(--fts-text) !important;
      font-weight: 500;
      .filter-action-button-icon,
      .sort-action-button-icon { color: var(--fts-text); }
    }
    .filter-action-button-count-badge {
      height: auto;
      min-width: 0;
      margin-left: 4px;
      padding: 0;
      border-radius: 0;
      background-color: transparent;
      color: inherit;
      font-size: inherit;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }
  }
  .filter-action-btn__active {
    background-color: transparent !important;
    border-color: transparent !important;
    color: var(--fts-text) !important;
    .filter-count-badge {
      width: auto;
      height: auto;
      margin-left: 4px;
      border-radius: 0;
      background-color: transparent;
      color: inherit;
    }
  }
  .list-view__content-secondary-header-actions > .fts-btn {
    height: 32px;
    min-height: 32px;
    width: 32px;
    padding: 0;
    color: var(--fts-default);
    &:hover { color: var(--fts-text); }
  }

  /* ---- R4 · table as a ledger: no card, hairlines, 36px rows, 13px ---- */
  .table-outter {
    padding: 0;
    border: 0;
    border-radius: 0;
    background-color: transparent;
  }
  .fts-data-table {
    .data-table__head .data-table__row { background-color: transparent; }
    .data-table__head .data-table__cell-placeholder { background-color: transparent; }

    .data-table__cell {
      height: 36px;
      padding: 0 12px;
      font-size: 0.8125rem;
      line-height: 20px;
      vertical-align: middle;
      border-bottom: 1px solid var(--fts-gray-100);

      /* only the first cell keeps a gutter (handle / drag); the trailing 108px hosts the facts + actions */
      &.data-table__cell--primary-cell {
        height: 36px;
        padding-left: 28px;
        padding-right: 108px;
      }
      &.data-table__cell--inline-header { height: 32px; }

      &.data-table__cell--header {
        height: 32px;
        padding: 0 12px;
        background-color: transparent;
        border-bottom: 1px solid var(--fts-gray-200);
        font-size: 0.75rem;
        font-weight: 500;
        color: var(--inistate-text-secondary);
        &:not(.data-table__cell--design) { padding-right: 12px; }
        &.data-table__cell--primary-cell { padding-left: 28px; padding-right: 12px; }
        &:hover:not(.other-col-resizing) { background-color: var(--inistate-color-neutral-50); }
        .resize-handle { width: 2px; background-color: var(--fts-gray-300); }
        &:has(.resize-handle-dragging) .resize-handle { background-color: var(--fts-text); }
        .fts-label-group.data-table__label {
          .label-group__label { color: inherit; font-weight: inherit; }
          .label-group__icon { color: var(--fts-default); }
        }
      }
    }
    /* sort glyph only on hover; the active ↑/↓ shows at rest in ink. Keyed on the glyph, not
       the template's --ghost class: isSortingHeader() returns 'idle' (not undefined) while no
       sort is set, so every column would otherwise show the neutral glyph at rest. */
    .data-table__sort {
      font-size: 11px;
      color: var(--fts-text);
      transition: opacity 0.15s ease;
      &--ghost,
      &:not(:has([data-icon="sort-up"], [data-icon="sort-down"])) {
        opacity: 0;
        color: var(--fts-default);
      }
    }
    .data-table__cell--header:hover .data-table__sort { opacity: 1; }
    /* With the glyph hidden at rest, the whole header cell is the sort target: the glyph's
       pseudo-element covers the cell (nearest positioned ancestor = the th), so a click anywhere
       on the header reaches the glyph's handler. Design-mode controls sit above it. */
    .data-table__cell--header .data-table__sort::after {
      content: "";
      position: absolute;
      inset: 0;
      cursor: pointer;
    }
    .data-table__cell--header .actions-group {
      position: relative;
      z-index: 1;
    }
    .data-table__cell--header .data-table__handle,
    .data-table__cell--header .resize-handle {
      z-index: 1;
    }

    .data-table_cell__handle {
      left: 4px;
      top: 50%;
      width: 20px;
      height: 20px;
      transform: translateY(-50%);
    }

    /* R6 — selection is a grey wash, not primary-50 */
    .data-table__row.data-table__row--selected {
      background-color: var(--inistate-color-neutral-100);
      &:hover { background-color: var(--inistate-color-neutral-100); }
      .data-table__cell-shortcut { background-color: var(--inistate-color-neutral-100); }
    }

    /* sticky trailing cell: hairline instead of shadow, delete on hover only */
    .data-table__cell-action__sticky {
      width: 96px;
      min-width: 96px;
      padding: 0 8px;
      text-align: right;
      border-left: 1px solid var(--fts-gray-100);
      border-bottom: 1px solid var(--fts-gray-100);
      box-shadow: none;
      &--header {
        padding: 0 8px;
        background-color: transparent !important;
      }
      .action-icon {
        display: inline-flex;
        margin-left: auto;
        font-size: 16px;
        opacity: 0;
        transition: opacity 0.15s ease, color 0.15s ease;
        &:hover { color: var(--fts-danger); background-color: transparent; }
      }
      &--header .action-icon { opacity: 0.35; &:hover { opacity: 1; color: var(--fts-text); } }
    }
    .data-table__row:hover .data-table__cell-action__sticky .action-icon { opacity: 1; }

    /* R5b — the hover cluster stays in its DOM but stops floating: facts (comment count,
       intention) read at rest at the cell's right edge; View / ⋮ appear on row hover as glyphs. */
    .data-table__cell-shortcut {
      top: 50%;
      right: 8px;
      height: 24px;
      min-width: 24px;         /* never collapses to 0×24 on a row with no facts: the Playwright
                                  helper hovers this box BEFORE the row reveals its actions
                                  (awt02 listingTableActions.clickEntryMoreActions) */
      justify-content: flex-end;
      padding: 0;
      gap: 2px;
      transform: translateY(-50%);
      background-color: transparent;
      z-index: 1;
      font-size: 0.75rem;
      .shortcut-container-comment {
        gap: 3px;
        color: var(--inistate-text-secondary);
        .shortcut-container-icon { font-size: 14px; }
      }
      .shortcut-container-intention .shortcut-container-icon { font-size: 14px; color: #b26a00; }
      .shortcut-container-btn {
        display: none;
        width: 24px;
        height: 24px;
        padding: 0;
        font-size: 0;            /* hides the "View" word; the glyph stays */
        background-color: transparent;
        .shortcut-container-icon { font-size: 16px; }
        &:hover { background-color: var(--inistate-color-neutral-100); }
      }
      .fts-standard-menu {
        display: none;
        .shortcut-container-btn-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--inistate-rounded-4);
          &:hover { background-color: var(--inistate-color-neutral-100); }
        }
      }
    }
    .data-table__row:hover .data-table__cell-shortcut {
      .shortcut-container-btn { display: flex; }
      .fts-standard-menu { display: inline-flex; }
    }

    /* R7 — tinted state chip (prop set in the template) sized to the row */
    .data-table__status > div {
      font-size: 0.75rem;
      line-height: 22px;
      font-weight: 500;
    }
    .fts-audit {
      font-size: 0.75rem;
      color: var(--inistate-text-secondary);
    }
  }

  /* ---- R8 · status strip: one fixed 36px line ---- */
  .list-view__content-pagination { margin-top: 0; }
  .fts-pagination {
    height: 36px;
    gap: 12px;
    font-size: 0.75rem;
    color: var(--inistate-text-secondary);
    border-top: 1px solid var(--fts-gray-100);
    .pagination-summary__value {
      color: var(--fts-text);
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }
    .page-control-container { gap: 2px; }
    .page-num-option {
      width: 28px;
      min-height: 28px;
      font-variant-numeric: tabular-nums;
      &--icon { color: var(--fts-default); }
      &__active {
        background-color: transparent;
        color: var(--fts-text);
        font-weight: 600;
      }
    }
    .toggle-container {
      gap: 4px;
      > span:first-child { display: none; }   /* "Row per page:" — the control says it */
    }
    .pagination-summary__note { color: var(--inistate-text-secondary); }
    .pagination-dropdown-toggle {
      padding: 0 4px;
      border: 0;
      background-color: transparent;
      color: var(--fts-text);
      .pagination-dropdown-toggle-icon {
        margin-left: 4px;
        font-size: 10px;
        color: var(--fts-default);
      }
    }
  }

  /* ---- R10 · design mode: view tabs weight-not-hue, grips at rest, ghost "New row" ---- */
  .view-bar { border-bottom-color: var(--fts-gray-200); }
  .view-tab {
    padding: 0 12px 6px;
    gap: 6px;
    font-size: 0.8125rem;
    line-height: 20px;
    color: var(--inistate-text-secondary);
  }
  .view-tab__active {
    color: var(--fts-text);
    font-weight: 600;
    border-bottom: 2px solid var(--fts-text);
  }
  .view-tab-actions {
    margin-top: 0;
    opacity: 0.35;
    transition: opacity 0.15s ease;
  }
  .view-bar:hover .view-tab-actions { opacity: 1; }
  .view-tab-action { padding: 0 4px; font-size: 16px; }
  .data-table__cell--inline-header .fts-btn {
    height: 32px;
    min-height: 32px;
    padding: 0 4px;
    font-size: 0.8125rem;
    letter-spacing: 0;
    color: var(--fts-default);
    &:hover { color: var(--fts-text); }
  }

  /* ---- R12 · drawer shell (the three design drawers are this page's only fts-drawer
          descendants; the nested column drawer inherits) ---- */
  .fts-drawer-main {
    background: rgba(0, 0, 0, 0.04);
    .fts-drawer-container {
      border-left: 1px solid var(--fts-gray-200);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
      .fts-drawer-content { padding: 0; }
    }

    /* header: 48px, 15/600 title, ghost close, no rule */
    .workflow-drawer-header,
    .listing-drawer-header,
    .info-drawer-header {
      padding-top: 0;
      hr { display: none; }
    }
    .workflow-drawer-header-content,
    .listing-drawer-header-content,
    .info-drawer-header-content {
      min-height: 48px;
      padding: 0 12px 0 20px;
    }
    .workflow-drawer-header-title,
    .listing-drawer-header-title,
    .info-drawer-header-title {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--fts-text);
    }

    /* R13 (class-level half) — Actions · States · Flows as the activity modal's nav ledger */
    .workflow-drawer-tabs {
      flex: 1;
      align-items: baseline;
      padding: 12px 0 0;
    }
    .workflow-drawer-tab {
      padding: 0 0 8px;
      border-bottom: 0;
      font-size: 0.8125rem;
      line-height: 20px;
      font-weight: 400;
      color: var(--fts-default);
      transition: color 0.15s ease;
      & + .workflow-drawer-tab::before {
        content: "·";
        padding: 0 10px;
        color: var(--fts-border);
      }
      &:hover { color: var(--fts-text); }
      &.active {
        color: var(--fts-text);
        font-weight: 600;
        border-bottom: 0;
      }
    }
    .workflow-drawer-header-content:has(.workflow-drawer-tabs) {
      align-items: flex-end;
      border-bottom: 1px solid var(--fts-gray-100);
    }

    /* bodies */
    .workflow-drawer-tab-content,
    .listing-drawer-content,
    .workflow-detail-content,
    .listing-detail-content,
    .info-drawer-content {
      padding: 12px 20px 16px;
    }
    /* "All actions" / "All states" → group label */
    .tab-header {
      margin: 4px 0 6px;
      .tab-title {
        font-size: 0.625rem;
        line-height: 16px;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--fts-form-helper);
      }
      .tab-hint { opacity: 0.6; }
      .tab-hint-icon { font-size: 14px; color: var(--fts-default); }
    }

    /* R14 — rows as ledger rows: no pill, hairlines, grip at rest 0.35, actions on hover */
    .workflow-item,
    .listing-item {
      min-height: 36px;
      margin-bottom: 0;
      padding: 0 0 0 24px;
      gap: 8px;
      background-color: transparent;
      border-radius: 0;
      border-bottom: 1px solid var(--fts-gray-100);
      font-size: 0.8125rem;
      color: var(--fts-text);
      &:hover {
        padding: 0 0 0 24px;
        background-color: var(--inistate-color-neutral-50);
      }
      &.default-action {
        background-color: transparent;
        &:hover { background-color: var(--inistate-color-neutral-50); }
      }
      &.editing { padding: 0 0 0 24px; }
      .workflow-item__handle,
      .listing-item__handle {
        left: 4px;
        visibility: visible;
        opacity: 0.35;
        font-size: 12px;
        transition: opacity 0.15s ease;
      }
      &:hover .workflow-item__handle,
      &:hover .listing-item__handle { opacity: 1; }
      .workflow-item__label {
        padding: 0;
        background-color: transparent;
        border-radius: 0;
        color: var(--fts-text-secondary);
        font-size: 0.75rem;
        font-weight: 400;
      }
      .workflow-item-name { font-size: 0.8125rem; }
      .workflow-inline-input { min-height: 32px; }
    }
    .ghost { background: var(--inistate-color-neutral-50); }
    .workflow-flow-path {
      font-size: 0.8125rem;
      .flow-direction { color: var(--fts-default); }
      .flow-activity-name { font-size: 0.8125rem; }
      .flow-conditional { font-size: 0.75rem; }
    }
    .workflow-dummy-block { height: 32px; }

    /* R16 — drawer empty states: one line, no illustration */
    .workflow-empty-state,
    .listing-empty-state {
      max-width: none;
      padding: 8px 0;
      text-align: left;
      .workflow-empty-state__icon,
      .listing-empty-state__icon { display: none; }
      .workflow-empty-state__title,
      .listing-empty-state__title {
        margin-bottom: 2px;
        font-size: 0.8125rem;
        line-height: 20px;
        font-weight: 500;
      }
      .workflow-empty-state__description,
      .listing-empty-state__description {
        margin-bottom: 8px;
        font-size: 0.75rem;
        line-height: 16px;
      }
    }

    /* R16 — drawer forms: 12px label above a 32px control, help below, one-line errors */
    .workflow-form-field,
    .listing-form-field,
    .info-form-field { margin-bottom: 14px; gap: 4px; }
    .workflow-form-label,
    .listing-form-label,
    .info-form-field > .info-form-label,
    .info-toggle-content > .info-form-label {
      font-size: 0.75rem;
      line-height: 16px;
      font-weight: 400;
      color: var(--fts-form-label);
    }
    .workflow-inline-input,
    .listing-inline-input,
    .listing-name-field,
    .listing-select,
    .info-form-input,
    .info-type-trigger {
      min-height: 32px;
      padding: 0 10px;
      font-size: 0.8125rem;
      line-height: 30px;
      border-color: var(--fts-gray-200);
      border-radius: var(--inistate-rounded-4);
    }
    .listing-name-input { font-size: 0.8125rem; }
    .workflow-form-hint,
    .info-form-helper,
    .info-type-hint {
      font-size: 0.75rem;
      line-height: 16px;
      color: var(--fts-text-secondary);
    }
    .workflow-form-error,
    .design-modal-form-error,
    .info-form-error,
    .info-option-error {
      padding: 0;
      background-color: transparent;
      border: 0;
      border-radius: 0;
      font-size: 0.75rem;
      line-height: 16px;
      color: var(--fts-danger);
    }
    .listing-designer-toggle-button {
      padding: 0;
      min-height: 36px;
      border: 0;
      border-bottom: 1px solid var(--fts-gray-100);
      border-radius: 0;
      background-color: transparent;
      font-size: 0.8125rem;
      &:hover { background-color: transparent; }
    }

    /* footer: 44px, hairline, Save is the only accent, Delete a ghost that reddens on hover */
    .info-drawer-actions,
    .workflow-drawer-actions,
    .listing-form-actions {
      min-height: 44px;
      padding: 0 12px;
      gap: 4px;
      border-top: 1px solid var(--fts-gray-100);
      background-color: transparent;
      .fts-btn {
        height: 32px;
        min-height: 32px;
        padding: 0 12px;
        font-size: 0.8125rem;
        letter-spacing: 0;
        text-transform: none;
      }
      > .fts-btn:first-child {
        color: var(--fts-default);
        &:hover { color: var(--fts-danger); }
      }
    }

    /* R15 (class-level half) — column drawer: cards → groups, switch → rule row, ghost add */
    .info-form-card {
      margin-bottom: 0;
      padding: 0;
      background-color: transparent;
      border: 0;
      border-radius: 0;
      > .info-form-label {
        display: block;
        margin: 20px 0 6px;
        font-size: 0.625rem;
        line-height: 16px;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--fts-form-helper);
      }
    }
    .info-select-switch {
      min-height: 36px;
      gap: 12px;
      margin-bottom: 0;
      border-bottom: 1px solid var(--fts-gray-100);
    }
    .info-select-switch-option {
      padding: 0;
      border-radius: 0;
      background: transparent;
      color: var(--fts-default);
      font-size: 0.8125rem;
      font-weight: 400;
      &:hover { background: transparent; color: var(--fts-text); }
      &.info-select-switch-option--active {
        background: transparent;
        color: var(--fts-text);
        font-weight: 600;
      }
    }
    .info-option-wrapper {
      min-height: 32px;
      border-bottom: 1px solid var(--fts-gray-100);
    }
    .info-option-handle { opacity: 0.35; }
    .info-option-wrapper:hover .info-option-handle { opacity: 1; }
    .info-option-name {
      padding: 0;
      border: 0;
      background: transparent;
      font-size: 0.8125rem;
    }
    .info-option-color .vue-swatches__trigger {
      height: 14px !important;
      width: 14px !important;
      min-width: 14px;
    }
    .info-option-add,
    .info-field-add {
      height: 32px;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      color: var(--fts-default) !important;
      font-size: 0.8125rem;
      letter-spacing: 0;
      &:hover { color: var(--fts-text) !important; }
    }
    .info-field-item {
      min-height: 32px;
      padding: 0;
      border-radius: 0;
      border-bottom: 1px solid var(--fts-gray-100);
      &:hover { background-color: var(--inistate-color-neutral-50); }
    }
    .info-field-handle { opacity: 0.35; }
    .info-field-item:hover .info-field-handle { opacity: 1; }
    .info-field-name { font-size: 0.8125rem; }
    .info-advanced-toggle {
      font-size: 0.8125rem;
      color: var(--fts-text-secondary);
    }
  }
}

/* design mode only: the row drag grip reveals on row hover. NOT at rest — the grip is a Material
   ligature ("drag_indicator") and a visible one joins the cell's innerText, which the Playwright
   Studio suite asserts reads exactly the cell value (VERIFY STATE). visibility:hidden keeps it
   out of innerText; opacity would not. */
.inistate-lite--design .fts-list-view-2 .fts-data-table .data-table__row:hover .data-table_cell__handle {
  visibility: visible;
}
</style>
